import { cloud } from "./cloud.js";
import { compressImage } from "./image-utils.js";
import { legacyCounts, migrateLegacySnapshot } from "./legacy-migration.js";

(function () {
  "use strict";

  const STORAGE = {
    auth: "qymj.webv1.auth",
    moments: "qymj.webv1.moments",
    draft: "qymj.webv1.draft",
    closures: "qymj.webv1.closures",
    closureDraft: "qymj.webv1.closureDraft",
    failNextSave: "qymj.webv1.failNextSave",
    customTypes: "qymj.webv1.customTypes",
    types: "qymj.webv1.types",
    hiddenTypes: "qymj.webv1.hiddenTypes",
    typeOrder: "qymj.webv1.typeOrder",
    tags: "qymj.webv1.tags",
    memoryShown: "qymj.webv1.memoryShown",
    replyDraft: "qymj.webv1.replyDraft",
    demoMode: "qymj.webv1.demoMode",
    cleanBaseline: "qymj.webv1.cleanBaseline.v1",
    dataVersion: "qymj.webv1.dataVersion",
    imageMeta: "qymj.webv1.imageMeta",
  };

  const DATA_VERSION = 3;
  const IMAGE_DB_NAME = "qymj.webv1.assets";
  const IMAGE_STORE = "images";

  const DEFAULT_TYPES = [
    { id: "type:life", name: "生活瞬间", description: "发生了一件想留下的事" },
    { id: "type:happiness", name: "小小幸福", description: "一个开心、满足或觉得生活很好看的瞬间" },
    { id: "type:learned", name: "今天学到", description: "学会、搞懂或意识到一件事" },
    { id: "type:feeling", name: "此刻感受", description: "记录当下真实的情绪和感受" },
    { id: "type:thought", name: "突然想到", description: "灵感、观点或突然冒出的念头" },
    { id: "type:person", name: "关于一个人", description: "和某个人有关的事情、关系或感受" },
    { id: "type:progress", name: "完成与进步", description: "完成、第一次做到或比以前更进一步" },
    { id: "type:unresolved", name: "没想明白", description: "困扰、纠结或暂时没有答案的问题" },
    { id: "type:misc", name: "随便记一下", description: "不想分类，只想先留下" },
  ];

  const TYPE_PROMPTS = {
    "小小幸福": "今天有没有什么很小，却让你觉得开心的事情？",
    "今天学到": "今天有什么新发现，或终于想明白的事？",
    "此刻感受": "此刻的心情，如果写下来，会是什么样？",
    "突然想到": "刚刚冒出的那个念头，要不要先留下？",
    "生活瞬间": "今天哪个普通瞬间，你还想多记一会儿？",
  };

  const MOCK_IMAGES = [
    { id: "tree", label: "街角树影" },
    { id: "meal", label: "晚餐" },
    { id: "desk", label: "书桌" },
    { id: "window", label: "窗边" },
  ];
  const DAILY_QUESTIONS = [
    "今天最想留下的是哪一个瞬间？",
    "今天有什么让你觉得幸福的小事？",
    "今天学到了什么？",
  ];

  const app = document.getElementById("app");
  const overlayRoot = document.getElementById("overlay-root");
  const toastRoot = document.getElementById("toast-root");

  const ui = {
    typeGuideOpen: false,
    globalTypeGuideOpen: false,
    inlineError: "",
    imageTarget: null,
    imageMomentId: "",
    imageUploadError: "",
    imageUploading: false,
    pendingDeleted: null,
    deleteTimer: null,
    toastTimer: null,
    timeView: "timeline",
    timeQuery: "",
    timeSearchTimer: null,
    timeFilterOpen: false,
    timeFilterType: "",
    timeFilterTag: "",
    timeFilterFrom: "",
    timeFilterTo: "",
    calendarMonth: todayKey().slice(0, 7),
    selectedDate: todayKey(),
    dateReturn: "time",
    detailReturn: "today",
    detailStack: [],
    memoryCurrentId: "",
    memoryMode: "random",
    memoryType: "",
    memorySeen: new Set(),
    replyExtrasOpen: false,
    todaySelectedId: "",
    timeSelectedDate: "all",
    workspaceMomentId: "",
    workspaceEditing: false,
    memoryMetaOpen: false,
    memoryDayOpen: false,
    memoryReplyOpen: false,
    memoryLinkedMomentId: "",
    myPanel: "",
    promptTypesExpanded: false,
    draftSaveState: "idle",
    draftSaveTimer: null,
    pendingImport: null,
    preImportBackup: null,
    startupPrompt: "",
    authMode: "login",
    authBusy: false,
    authError: "",
    authNotice: "",
    migrationPrompted: false,
    migrationBusy: false,
    migrationError: "",
    pendingSaveId: "",
    pendingReplyIds: new Map(),
  };

  let volatileDraft = null;
  const volatileEditDrafts = new Map();
  const imageUrlCache = new Map();
  let imageDbPromise = null;

  function read(key, fallback) {
    try {
      const value = localStorage.getItem(key);
      return value == null ? fallback : JSON.parse(value);
    } catch (_) {
      return fallback;
    }
  }

  function write(key, value) {
    localStorage.setItem(key, JSON.stringify(value));
  }

  function tryWrite(key, value) {
    try {
      write(key, value);
      return true;
    } catch (_) {
      return false;
    }
  }

  function remove(key) {
    localStorage.removeItem(key);
  }

  function openImageDb() {
    if (!globalThis.indexedDB) return Promise.reject(new Error("当前浏览器不支持本地图片存储"));
    if (imageDbPromise) return imageDbPromise;
    imageDbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(IMAGE_DB_NAME, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(IMAGE_STORE)) db.createObjectStore(IMAGE_STORE, { keyPath: "id" });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error("无法打开本地图片存储"));
    });
    return imageDbPromise;
  }

  async function getImageAsset(id) {
    const db = await openImageDb();
    return new Promise((resolve, reject) => {
      const request = db.transaction(IMAGE_STORE, "readonly").objectStore(IMAGE_STORE).get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error || new Error("无法读取图片"));
    });
  }

  async function putImageAsset(file) {
    const id = `img:${makeId()}`;
    const record = { id, blob: file, name: file.name || "生活图片", type: file.type || "image/*", size: file.size || 0, createdAt: new Date().toISOString() };
    const db = await openImageDb();
    await new Promise((resolve, reject) => {
      const request = db.transaction(IMAGE_STORE, "readwrite").objectStore(IMAGE_STORE).put(record);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error || new Error("图片保存失败"));
    });
    const meta = read(STORAGE.imageMeta, {});
    tryWrite(STORAGE.imageMeta, { ...meta, [id]: { name: record.name, type: record.type, size: record.size, createdAt: record.createdAt } });
    return id;
  }

  async function putImportedImageAsset(record) {
    const db = await openImageDb();
    await new Promise((resolve, reject) => {
      const request = db.transaction(IMAGE_STORE, "readwrite").objectStore(IMAGE_STORE).put(record);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error || new Error("备份图片恢复失败"));
    });
  }

  async function deleteImageAsset(id) {
    if (!String(id).startsWith("img:")) return;
    const db = await openImageDb();
    await new Promise((resolve, reject) => {
      const request = db.transaction(IMAGE_STORE, "readwrite").objectStore(IMAGE_STORE).delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error || new Error("图片草稿清理失败"));
    });
    const cached = imageUrlCache.get(id);
    if (cached) URL.revokeObjectURL(cached);
    imageUrlCache.delete(id);
    const meta = read(STORAGE.imageMeta, {}); delete meta[id]; tryWrite(STORAGE.imageMeta, meta);
  }

  async function getAllImageAssets() {
    const db = await openImageDb();
    return new Promise((resolve, reject) => {
      const request = db.transaction(IMAGE_STORE, "readonly").objectStore(IMAGE_STORE).getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error || new Error("无法读取本地图片"));
    });
  }

  function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error("图片编码失败"));
      reader.readAsDataURL(blob);
    });
  }

  function dataUrlToBlob(dataUrl) {
    const [header, encoded] = String(dataUrl).split(",");
    const mime = header.match(/^data:([^;]+);base64$/)?.[1];
    if (!mime || !encoded) throw new Error("备份中存在无法识别的图片");
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return new Blob([bytes], { type: mime });
  }

  async function clearImageAssets() {
    imageUrlCache.forEach((url) => URL.revokeObjectURL(url));
    imageUrlCache.clear();
    try {
      const db = await openImageDb();
      await new Promise((resolve, reject) => {
        const request = db.transaction(IMAGE_STORE, "readwrite").objectStore(IMAGE_STORE).clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error || new Error("无法清空图片"));
      });
    } catch (_) {}
  }

  function imageLabel(id) {
    return cloud.imageMeta(id)?.name || MOCK_IMAGES.find((item) => item.id === id)?.label || read(STORAGE.imageMeta, {})[id]?.name || "生活图片";
  }

  function imageThumbHtml(id, className, label = imageLabel(id)) {
    if (String(id).startsWith("cloud:") || String(id).startsWith("img:")) return `<img class="${className} local-photo" data-local-image="${esc(id)}" alt="${esc(label)}" />`;
    return `<span class="${className} photo-${esc(id)}" aria-label="${esc(label)}"></span>`;
  }

  async function hydrateLocalImages(root = document) {
    const nodes = [...root.querySelectorAll("img[data-local-image]:not([src])")];
    await Promise.all(nodes.map(async (node) => {
      const id = node.dataset.localImage;
      try {
        let url = imageUrlCache.get(id);
        if (!url) {
          if (String(id).startsWith("cloud:")) url = await cloud.signedImageUrl(id);
          else {
            const asset = await getImageAsset(id);
            if (!asset?.blob) return node.classList.add("is-missing");
            url = URL.createObjectURL(asset.blob);
          }
          imageUrlCache.set(id, url);
        }
        if (node.isConnected) node.src = url;
      } catch (_) {
        node.classList.add("is-missing");
      }
    }));
  }

  let imageHydrationQueued = false;
  function queueImageHydration() {
    if (imageHydrationQueued) return;
    imageHydrationQueued = true;
    requestAnimationFrame(() => {
      imageHydrationQueued = false;
      hydrateLocalImages().catch(() => {});
    });
  }

  function typeIdByName(name, items = getTypeItems()) {
    const clean = String(name || "").trim();
    return items.find((item) => item.name === clean)?.id || "";
  }

  function tagIdByName(name, items = getTagItems()) {
    const clean = String(name || "").trim();
    return items.find((item) => item.name === clean)?.id || "";
  }

  function canonicalEditorData(data = {}, sourceMomentId = null) {
    const types = getTypeItems();
    const tags = getTagItems();
    const hasLegacyType = Object.prototype.hasOwnProperty.call(data, "type");
    const hasLegacyTags = Object.prototype.hasOwnProperty.call(data, "tags");
    const hasLegacyTime = Object.prototype.hasOwnProperty.call(data, "occurrenceAt");
    const hasLegacyBody = Object.prototype.hasOwnProperty.call(data, "body");
    const recordTypeId = String(hasLegacyType ? typeIdByName(data.type, types) : (data.recordTypeId || ""));
    const legacyTags = hasLegacyTags && Array.isArray(data.tags) ? data.tags : [];
    const tagIds = !hasLegacyTags && Array.isArray(data.tagIds)
      ? data.tagIds.map(String).filter((id) => tags.some((item) => item.id === id))
      : legacyTags.map((name) => tagIdByName(name, tags)).filter(Boolean);
    return {
      content: String(hasLegacyBody ? data.body : (data.content ?? "")),
      images: Array.isArray(data.images) ? [...new Set(data.images.filter(Boolean).map(String))].slice(0, 3) : [],
      recordTypeId: types.some((item) => item.id === recordTypeId) ? recordTypeId : "",
      tagIds: [...new Set(tagIds)],
      occurredAt: (hasLegacyTime ? data.occurrenceAt : data.occurredAt) || nowLocalValue(),
      sourceMomentId: data.sourceMomentId || data.sourceId || sourceMomentId || null,
      expanded: Boolean(data.expanded),
      updatedAt: Number(data.updatedAt || Date.now()),
    };
  }

  function normalizeMoment(moment) {
    const editor = canonicalEditorData(moment, moment?.sourceMomentId || moment?.sourceId || null);
    return withLegacyAliases({
      id: String(moment?.id || makeId()),
      content: editor.content,
      images: editor.images,
      recordTypeId: editor.recordTypeId || null,
      tagIds: editor.tagIds,
      occurredAt: editor.occurredAt,
      createdAt: moment?.createdAt || new Date().toISOString(),
      sourceMomentId: editor.sourceMomentId || null,
    });
  }

  function withLegacyAliases(value) {
    if (!value || typeof value !== "object") return value;
    const descriptors = {
      body: { get() { return this.content; }, set(next) { this.content = String(next ?? ""); }, configurable: true },
      type: { get() { return recordTypeName(this.recordTypeId); }, set(next) { this.recordTypeId = typeIdByName(next) || null; }, configurable: true },
      tags: { get() { return (this.tagIds || []).map(tagName).filter(Boolean); }, set(next) { this.tagIds = Array.isArray(next) ? next.map((name) => tagIdByName(name)).filter(Boolean) : []; }, configurable: true },
      occurrenceAt: { get() { return this.occurredAt; }, set(next) { this.occurredAt = next || nowLocalValue(); }, configurable: true },
    };
    Object.defineProperties(value, descriptors);
    return value;
  }

  function withEditorAliases(value) {
    return withLegacyAliases(value);
  }

  function migrateStoredData() {
    const currentVersion = Number(read(STORAGE.dataVersion, 0));
    const rawMoments = Array.isArray(read(STORAGE.moments, [])) ? read(STORAGE.moments, []) : [];

    const typeItems = getTypeItems();
    const legacyTypeNames = [...new Set(rawMoments.map((moment) => String(moment?.type || "").trim()).filter(Boolean))];
    legacyTypeNames.forEach((name) => {
      if (!typeItems.some((item) => item.name === name)) typeItems.push({ id: `type:legacy:${makeId()}`, name, description: "由历史记录保留", isArchived: true, isDefault: false, createdAt: new Date().toISOString() });
    });
    tryWrite(STORAGE.types, typeItems);
    tryWrite(STORAGE.customTypes, []);
    tryWrite(STORAGE.hiddenTypes, []);

    const tagItems = getTagItems();
    const legacyTagNames = [...new Set(rawMoments.flatMap((moment) => Array.isArray(moment?.tags) ? moment.tags : []).map((name) => String(name).trim()).filter(Boolean))];
    legacyTagNames.forEach((name) => {
      if (!tagItems.some((item) => item.name === name)) tagItems.push({ id: `tag:${makeId()}`, name, isArchived: false, createdAt: new Date().toISOString() });
    });
    tryWrite(STORAGE.tags, tagItems);

    const normalized = rawMoments.map(normalizeMoment).filter((moment) => moment.content.trim());
    if (currentVersion < DATA_VERSION || JSON.stringify(normalized) !== JSON.stringify(rawMoments)) tryWrite(STORAGE.moments, normalized);

    const draft = read(STORAGE.draft, null);
    if (draft) tryWrite(STORAGE.draft, canonicalEditorData(draft));
    const replyDraft = read(STORAGE.replyDraft, null);
    if (replyDraft) tryWrite(STORAGE.replyDraft, { ...canonicalEditorData(replyDraft, replyDraft.sourceId), sourceId: replyDraft.sourceId || replyDraft.sourceMomentId });
    Object.keys(localStorage).filter((key) => key.startsWith("qymj.webv1.edit.")).forEach((key) => {
      const value = read(key, null);
      if (value) tryWrite(key, canonicalEditorData(value));
    });

    const closures = read(STORAGE.closures, {});
    if (!closures || Array.isArray(closures) || typeof closures !== "object") tryWrite(STORAGE.closures, {});
    tryWrite(STORAGE.dataVersion, DATA_VERSION);
  }

  function clearPrototypeUserData(clearImages = true) {
    [
      STORAGE.moments, STORAGE.draft, STORAGE.closures, STORAGE.closureDraft,
      STORAGE.failNextSave, STORAGE.customTypes, STORAGE.hiddenTypes,
      STORAGE.types, STORAGE.typeOrder, STORAGE.tags, STORAGE.memoryShown, STORAGE.replyDraft,
      STORAGE.demoMode, STORAGE.imageMeta,
      "qymj.webv1.demoSeeded.v1",
    ].forEach(remove);
    Object.keys(localStorage).filter((key) => key.startsWith("qymj.webv1.edit.")).forEach(remove);
    write(STORAGE.moments, []);
    write(STORAGE.closures, {});
    write(STORAGE.customTypes, []);
    write(STORAGE.types, DEFAULT_TYPES.map((item) => ({ ...item, isArchived: false, isDefault: true, createdAt: "2026-01-01T00:00:00.000Z" })));
    write(STORAGE.hiddenTypes, []);
    write(STORAGE.typeOrder, []);
    write(STORAGE.tags, []);
    write(STORAGE.memoryShown, {});
    if (clearImages) clearImageAssets().catch(() => {});
    volatileDraft = null;
    volatileEditDrafts.clear();
    ui.typeGuideOpen = false;
    ui.globalTypeGuideOpen = false;
    ui.inlineError = "";
    ui.imageUploadError = "";
    ui.imageUploading = false;
    ui.memoryCurrentId = "";
    ui.memoryMode = "random";
    ui.memoryType = "";
    ui.memorySeen.clear();
    ui.replyExtrasOpen = false;
    ui.todaySelectedId = "";
    ui.timeSelectedDate = "all";
    ui.timeQuery = "";
    clearTimeout(ui.timeSearchTimer);
    ui.timeFilterOpen = false;
    ui.timeFilterType = "";
    ui.timeFilterTag = "";
    ui.timeFilterFrom = "";
    ui.timeFilterTo = "";
    ui.workspaceMomentId = "";
    ui.workspaceEditing = false;
    ui.memoryMetaOpen = false;
    ui.memoryDayOpen = false;
    ui.memoryReplyOpen = false;
    ui.memoryLinkedMomentId = "";
    ui.myPanel = "";
    ui.promptTypesExpanded = false;
    ui.draftSaveState = "idle";
    clearTimeout(ui.draftSaveTimer);
    ui.pendingImport = null;
  }

  function prepareCleanBaseline() {
    const params = new URLSearchParams(location.search);
    const resetRequested = params.get("reset") === "1";
    const seedRequested = params.get("seed") === "1";
    if (resetRequested || seedRequested) {
      write(STORAGE.auth, true);
      ui.myPanel = "data";
      ui.startupPrompt = resetRequested ? "clear" : "seed";
      history.replaceState(null, "", `${location.pathname}#/me`);
      return;
    }
    if (read(STORAGE.cleanBaseline, false)) return;
    const authState = read(STORAGE.auth, false);
    clearPrototypeUserData();
    write(STORAGE.auth, authState);
    write(STORAGE.cleanBaseline, true);
  }

  function esc(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function nowLocalValue(date = new Date()) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  function todayKey() {
    return nowLocalValue().slice(0, 10);
  }

  function formatDateTitle(date = new Date()) {
    return new Intl.DateTimeFormat("zh-CN", { month: "long", day: "numeric", weekday: "long" }).format(date);
  }

  function formatDateTime(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value.replace("T", " ");
    return new Intl.DateTimeFormat("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
  }

  function formatTime(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(11, 16);
    return new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
  }

  function parseLocalDate(key) {
    const [year, month, day] = String(key).split("-").map(Number);
    return new Date(year, month - 1, day, 12, 0, 0);
  }

  function formatGroupDate(key, withYear = false) {
    const date = parseLocalDate(key);
    return new Intl.DateTimeFormat("zh-CN", { year: withYear ? "numeric" : undefined, month: "long", day: "numeric", weekday: "short" }).format(date);
  }

  function formatSourceDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
    return new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "long", day: "numeric" }).format(date);
  }

  function relativePast(value) {
    const dateKey = String(value).slice(0, 10);
    const days = Math.max(0, Math.round((parseLocalDate(todayKey()) - parseLocalDate(dateKey)) / 86400000));
    if (days === 0) return "今天";
    if (days === 1) return "昨天";
    if (days < 30) return `${days} 天前`;
    if (days < 365) return `约 ${Math.max(1, Math.round(days / 30))} 个月前`;
    return `约 ${Math.max(1, Math.round(days / 365))} 年前`;
  }

  function memoryTimeSense(value) {
    const then = new Date(value);
    const now = new Date();
    if (Number.isNaN(then.getTime())) return "从过去重新来到今天";
    const days = Math.max(1, Math.round((parseLocalDate(todayKey()) - parseLocalDate(String(value).slice(0, 10))) / 86400000));
    if (days < 7) return `${days} 天前留下`;
    if (days < 30) return `大约 ${Math.max(1, Math.round(days / 7))} 周前留下`;
    const months = Math.max(1, (now.getFullYear() - then.getFullYear()) * 12 + now.getMonth() - then.getMonth());
    const sameDay = now.getDate() === then.getDate();
    if (months < 12) return sameDay ? `${months} 个月前的今天` : `大约 ${months} 个月前留下`;
    const years = Math.max(1, Math.floor(months / 12));
    return sameDay && now.getMonth() === then.getMonth() ? `${years} 年前的今天` : `大约 ${years} 年前留下`;
  }

  function formatMemoryDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return formatSourceDate(value);
    return new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "long", day: "numeric", weekday: "long" }).format(date);
  }

  function localValueDaysAgo(daysAgo, hour, minute) {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    date.setHours(hour, minute, 0, 0);
    return nowLocalValue(date);
  }

  function isoFromLocal(value, offsetMinutes = 0) {
    const date = new Date(value);
    date.setMinutes(date.getMinutes() + offsetMinutes);
    return date.toISOString();
  }

  function seedDemoSpace() {
    const oldSourceId = "demo-source-old";
    const moments = [
      {
        id: "demo-today-sunset",
        body: "下班路上的晚霞，像岛屿边缘慢慢亮起来。站在路口多看了一会儿，今天好像也跟着柔软了一点。",
        images: ["window"], type: "生活瞬间", tags: ["下班路上"],
        occurrenceAt: localValueDaysAgo(0, 17, 42), createdAt: isoFromLocal(localValueDaysAgo(0, 17, 42), 3), sourceMomentId: null,
      },
      {
        id: "demo-today-coffee",
        body: "下午喝到很好喝的咖啡，店里的绿植刚好被阳光照亮。",
        images: ["tree"], type: "小小幸福", tags: ["日常"],
        occurrenceAt: localValueDaysAgo(0, 12, 18), createdAt: isoFromLocal(localValueDaysAgo(0, 12, 18), 2), sourceMomentId: null,
      },
      {
        id: "demo-today-thought",
        body: "卡住的问题终于松动了一点。也许答案一直都在，只是今天才刚好看见。",
        images: [], type: "突然想到", tags: ["工作", "成长"],
        occurrenceAt: localValueDaysAgo(0, 9, 6), createdAt: isoFromLocal(localValueDaysAgo(0, 9, 6), 4), sourceMomentId: null,
      },
      {
        id: "demo-yesterday-friend",
        body: "和朋友吃饭时聊到一件小时候的事情。原来我们记住的常常不是大事，是当时有人坐在旁边，认真听完了那句话。",
        images: ["meal"], type: "关于一个人", tags: ["朋友"],
        occurrenceAt: localValueDaysAgo(1, 20, 24), createdAt: isoFromLocal(localValueDaysAgo(1, 22, 6)), sourceMomentId: null,
      },
      {
        id: "demo-week-lesson",
        body: "今天学到一件很实用的小事：先把问题写成一句能回答的话，很多混乱就会自己退开。",
        images: ["desk"], type: "今天学到", tags: ["产品", "工作"],
        occurrenceAt: localValueDaysAgo(8, 15, 36), createdAt: isoFromLocal(localValueDaysAgo(8, 16, 8)), sourceMomentId: null,
      },
      {
        id: "demo-month-opportunity",
        body: "最近在准备新的工作机会。整理过去做过的事情时，第一次清楚地看见：那些零散的判断和选择，其实已经慢慢连成了一条路。",
        images: [], type: "完成与进步", tags: ["工作", "成长"],
        occurrenceAt: localValueDaysAgo(43, 23, 12), createdAt: isoFromLocal(localValueDaysAgo(42, 0, 3)), sourceMomentId: null,
      },
      {
        id: oldSourceId,
        body: "今天第一次独自完成了整个方案汇报。开口前还是很紧张，但讲到第三页时忽然稳了下来。想记住这一刻。",
        images: [], type: "完成与进步", tags: ["工作", "第一次"],
        occurrenceAt: localValueDaysAgo(188, 18, 35), createdAt: isoFromLocal(localValueDaysAgo(188, 18, 46)), sourceMomentId: null,
      },
      {
        id: "demo-linked-now",
        body: "重新看到那次汇报，才发现自己已经比当时从容很多。成长有时很安静，回头看才知道走了多远。",
        images: [], type: "此刻感受", tags: ["工作", "成长"],
        occurrenceAt: localValueDaysAgo(12, 21, 10), createdAt: isoFromLocal(localValueDaysAgo(12, 21, 14)), sourceMomentId: oldSourceId,
      },
    ];
    clearPrototypeUserData();
    write(STORAGE.moments, moments);
    write(STORAGE.tags, ["日常", "下班路上", "朋友", "产品", "工作", "成长", "第一次"]);
    write(STORAGE.customTypes, [{ id: "custom:值得记住", name: "值得记住", description: "想为以后多留一会儿的事情" }]);
    write(STORAGE.closures, {
      [todayKey()]: {
        date: todayKey(),
        answers: ["下班时看见的那片晚霞。", "咖啡很好喝，也终于把卡住的问题想明白了一点。", "把问题说清楚，答案就会靠近。"],
        finalLine: "今天没有发生大事，但有几件小事让我愿意记住。",
        updatedAt: new Date().toISOString(),
      },
    });
    write(STORAGE.auth, true);
    write(STORAGE.demoMode, true);
    migrateStoredData();
    ui.memoryCurrentId = oldSourceId;
    ui.memorySeen.clear();
  }

  function startEmptySpace() {
    clearPrototypeUserData();
    write(STORAGE.auth, true);
    write(STORAGE.demoMode, false);
  }

  function makeId() {
    return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function getTypeItems() {
    if (cloud.state.user) return [...cloud.state.snapshot.types].sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
    const stored = read(STORAGE.types, []);
    const hidden = new Set(read(STORAGE.hiddenTypes, []));
    const oldCustoms = Array.isArray(read(STORAGE.customTypes, [])) ? read(STORAGE.customTypes, []) : [];
    const storedItems = Array.isArray(stored) ? stored.filter((item) => item && typeof item === "object") : [];
    const existingByName = new Map(storedItems.map((item) => [String(item.name || "").trim(), item]));
    const defaults = DEFAULT_TYPES.map((item) => {
      const existing = existingByName.get(item.name) || {};
      return {
        id: item.id,
        name: item.name,
        description: existing.description || item.description,
        isArchived: Boolean(existing.isArchived ?? existing.hidden ?? hidden.has(item.name)),
        isDefault: true,
        createdAt: existing.createdAt || "2026-01-01T00:00:00.000Z",
      };
    });
    const customSources = [...storedItems.filter((item) => !DEFAULT_TYPES.some((preset) => preset.name === item.name)), ...oldCustoms];
    const customs = [];
    customSources.forEach((item) => {
      const name = String(item?.name || "").trim();
      if (!name || customs.some((existing) => existing.name === name)) return;
      customs.push({
        id: String(item.id || `type:${makeId()}`),
        name,
        description: String(item.description || "自定义记录类型"),
        isArchived: Boolean(item.isArchived ?? item.hidden),
        isDefault: false,
        createdAt: item.createdAt || new Date().toISOString(),
      });
    });
    const all = [...defaults, ...customs];
    const order = read(STORAGE.typeOrder, []);
    const rank = new Map(order.map((id, index) => [id, index]));
    const sorted = all.sort((a, b) => (rank.get(a.id) ?? 999) - (rank.get(b.id) ?? 999));
    return sorted;
  }

  function getAvailableTypes() {
    return getTypeItems().filter((item) => !item.isArchived);
  }

  function getTagItems() {
    if (cloud.state.user) return [...cloud.state.snapshot.tags];
    const stored = read(STORAGE.tags, []);
    if (!Array.isArray(stored)) return [];
    const seen = new Set();
    return stored.map((item) => typeof item === "string"
      ? { id: `tag:legacy:${encodeURIComponent(item)}`, name: item, isArchived: false, createdAt: new Date().toISOString() }
      : { id: String(item.id || `tag:${makeId()}`), name: String(item.name || "").trim(), isArchived: Boolean(item.isArchived), createdAt: item.createdAt || new Date().toISOString() })
      .filter((item) => item.name && !seen.has(item.id) && seen.add(item.id));
  }

  function getTags() {
    return getTagItems().filter((item) => !item.isArchived);
  }

  function recordTypeName(id) {
    return getTypeItems().find((item) => item.id === id)?.name || "";
  }

  function tagName(id) {
    return getTagItems().find((item) => item.id === id)?.name || "";
  }

  function momentTypeName(moment) {
    return recordTypeName(moment?.recordTypeId);
  }

  function momentTagNames(moment) {
    return (moment?.tagIds || []).map(tagName).filter(Boolean);
  }

  async function ensureTagExists(raw) {
    const name = String(raw || "").trim();
    if (!name) return "";
    const tags = getTagItems();
    const existing = tags.find((item) => item.name === name);
    if (existing) return existing.id;
    if (cloud.state.user) return cloud.createTag(name);
    const item = { id: `tag:${makeId()}`, name, isArchived: false, createdAt: new Date().toISOString() };
    write(STORAGE.tags, [...tags, item]);
    return item.id;
  }

  function emptyDraft() {
    return withEditorAliases({ content: "", images: [], recordTypeId: "", tagIds: [], occurredAt: nowLocalValue(), sourceMomentId: null, expanded: false, updatedAt: Date.now() });
  }

  function getDraft() {
    if (volatileDraft) return withEditorAliases({ ...emptyDraft(), ...canonicalEditorData(volatileDraft) });
    const draft = read(STORAGE.draft, null);
    return draft ? withEditorAliases({ ...emptyDraft(), ...canonicalEditorData(draft) }) : emptyDraft();
  }

  function saveDraft(patch) {
    const next = withEditorAliases({ ...canonicalEditorData({ ...getDraft(), ...patch }), updatedAt: Date.now() });
    if (tryWrite(STORAGE.draft, next)) {
      volatileDraft = null;
      ui.draftSaveState = "saving";
      clearTimeout(ui.draftSaveTimer);
      ui.draftSaveTimer = setTimeout(() => {
        ui.draftSaveState = "saved";
        updateDraftIndicator(getDraft());
        updateGlobalDraftIndicator(getDraft());
      }, 320);
    } else {
      volatileDraft = next;
      ui.inlineError = "浏览器暂时无法写入本地存储。正文仍保留在当前页面，请先不要刷新。";
    }
    updateDraftIndicator(next);
    updateGlobalDraftIndicator(next);
    updateComposerProgressive(next);
    return next;
  }

  function draftSaveLabel() {
    if (ui.draftSaveState === "saving") return "正在保存…";
    if (ui.draftSaveState === "saved") return "已保存";
    return "已保存";
  }

  function hasDraftContent(draft) {
    return Boolean(draft.content.trim() || draft.images.length || draft.recordTypeId || draft.tagIds.length || draft.expanded);
  }

  function updateDraftIndicator(draft) {
    const node = document.querySelector("[data-draft-indicator]");
    if (!node) return;
    node.hidden = !hasDraftContent(draft);
    node.classList.toggle("is-saving", ui.draftSaveState === "saving");
    node.innerHTML = `<span class="draft-dot"></span>${draftSaveLabel()}`;
  }

  function updateGlobalDraftIndicator(draft) {
    const node = document.querySelector("[data-global-draft-indicator]");
    if (!node) return;
    node.hidden = !hasDraftContent(draft);
    node.classList.toggle("is-saving", ui.draftSaveState === "saving");
    node.innerHTML = `<span class="draft-dot"></span>${draftSaveLabel()}`;
  }

  function updateComposerProgressive(draft) {
    const visible = hasDraftContent(draft);
    const actions = document.querySelector("[data-progressive-actions]");
    if (actions) actions.hidden = !visible;
    const firstHint = document.querySelector(".composer-first-hint");
    if (firstHint) firstHint.hidden = visible;
    const typeNote = document.querySelector("[data-type-note]");
    if (typeNote) typeNote.hidden = !visible;
  }

  function getMoments() {
    if (cloud.state.user) return cloud.state.snapshot.moments.map(normalizeMoment);
    const moments = read(STORAGE.moments, []);
    return Array.isArray(moments) ? moments.map(normalizeMoment) : [];
  }

  function setMoments(moments) {
    if (cloud.state.user) { cloud.setMomentCache(moments.map(normalizeMoment)); return; }
    write(STORAGE.moments, moments.map(normalizeMoment));
  }

  function getClosures() {
    return cloud.state.user ? cloud.state.snapshot.closures : read(STORAGE.closures, {});
  }

  function getLegacySnapshot() {
    const rawTypes = read(STORAGE.types, []);
    const rawTags = read(STORAGE.tags, []);
    const moments = Array.isArray(read(STORAGE.moments, [])) ? read(STORAGE.moments, []) : [];
    const types = (Array.isArray(rawTypes) && rawTypes.length ? rawTypes : DEFAULT_TYPES).map((item) => typeof item === "string" ? { id: `type:legacy:${item}`, name: item } : item).filter((item) => item?.name);
    const tags = (Array.isArray(rawTags) ? rawTags : []).map((item) => typeof item === "string" ? { id: `tag:legacy:${encodeURIComponent(item)}`, name: item, isArchived: false } : item).filter((item) => item?.name);
    const closures = read(STORAGE.closures, {});
    const imageIds = [...new Set(moments.flatMap((moment) => moment.images || []).filter((id) => String(id).startsWith("img:")))];
    return { moments, types, tags, closures: closures && typeof closures === "object" && !Array.isArray(closures) ? closures : {}, imageIds };
  }

  function hasLegacyUserData(snapshot = getLegacySnapshot()) {
    const customTypes = snapshot.types.filter((item) => !DEFAULT_TYPES.some((preset) => preset.name === item.name));
    return Boolean(snapshot.moments.length || snapshot.tags.length || customTypes.length || Object.keys(snapshot.closures).length || snapshot.imageIds.length);
  }

  function showLegacyMigrationOffer() {
    if (!cloud.state.user || ui.migrationBusy) return;
    const snapshot = getLegacySnapshot();
    if (!hasLegacyUserData(snapshot)) return;
    const dismissedKey = `qymj.webv1.migration-dismissed:${cloud.state.user.id}`;
    if (sessionStorage.getItem(dismissedKey) === "1") return;
    const counts = legacyCounts(snapshot);
    overlayRoot.innerHTML = `<div class="modal-backdrop"><section class="modal migration-modal" role="dialog" aria-modal="true" aria-labelledby="migration-title" data-stop-close>
      <span class="modal-symbol">${icon("upload")}</span><h2 id="migration-title">迁移本机记录到当前账号</h2>
      <p>检测到这个浏览器里有旧版数据：${counts.moments} 条 Moment、${counts.images} 张图片、${counts.tags} 个标签、${counts.closures} 份每日收束。</p>
      <div class="import-impact"><strong>迁移失败不会删除本机原始数据</strong><small>迁移可以重试；完成后会校验 Moment、图片、收束和来源关系数量。</small></div>
      ${ui.migrationError ? `<div class="inline-error" role="alert">${esc(ui.migrationError)}</div>` : ""}
      <div class="modal-actions"><button class="btn" data-action="dismiss-migration">稍后再说</button><button class="btn btn-primary" data-action="migrate-local-data">开始迁移</button></div>
    </section></div>`;
  }

  async function runLegacyMigration() {
    if (ui.migrationBusy) return;
    const snapshot = getLegacySnapshot();
    ui.migrationBusy = true; ui.migrationError = "";
    const progress = (message) => {
      overlayRoot.innerHTML = `<div class="modal-backdrop"><section class="modal migration-modal" role="dialog" aria-modal="true" aria-labelledby="migration-progress" data-stop-close><span class="modal-symbol">${icon("upload")}</span><h2 id="migration-progress">正在迁移本机记录</h2><p>${esc(message)}</p><div class="migration-progress-line"><span></span></div><small>请保持当前页面打开，原始本地数据不会在迁移中被删除。</small></section></div>`;
    };
    try {
      const result = await migrateLegacySnapshot(snapshot, getImageAsset, progress);
      const counts = result.counts;
      overlayRoot.innerHTML = `<div class="modal-backdrop"><section class="modal migration-modal" role="dialog" aria-modal="true" aria-labelledby="migration-done" data-stop-close><span class="modal-symbol">${icon("check")}</span><h2 id="migration-done">本机记录已迁移并校验</h2><p>${counts.moments} 条 Moment、${counts.images} 张图片、${counts.closures} 份每日收束和 ${counts.links} 条来源关系已进入当前账号。</p><div class="import-impact"><strong>现在可以选择保留或清理旧本地数据</strong><small>清理只影响这个浏览器里的旧版副本，不会删除云端账号数据。</small></div><div class="modal-actions"><button class="btn" data-action="keep-legacy-data">暂时保留</button><button class="btn btn-primary" data-action="clear-migrated-data">清理旧本地数据</button></div></section></div>`;
    } catch (error) {
      ui.migrationError = error?.message || "迁移未完成，可以稍后重试";
      showLegacyMigrationOffer();
    } finally { ui.migrationBusy = false; }
  }

  async function clearMigratedLocalData() {
    clearPrototypeUserData(false);
    await clearImageAssets();
    overlayRoot.innerHTML = "";
    render();
    showToast("旧本地副本已清理，云端数据不受影响");
  }

  function sortMoments(moments) {
    return [...moments].sort((a, b) => {
      const occurrence = String(b.occurredAt).localeCompare(String(a.occurredAt));
      if (occurrence !== 0) return occurrence;
      return String(b.createdAt).localeCompare(String(a.createdAt));
    });
  }

  function timeFiltersActive() {
    return Boolean(ui.timeQuery.trim() || ui.timeFilterType || ui.timeFilterTag || ui.timeFilterFrom || ui.timeFilterTo);
  }

  function filteredTimeMoments(moments) {
    const query = ui.timeQuery.trim().toLocaleLowerCase("zh-CN");
    return moments.filter((moment) => {
      const date = String(moment.occurredAt).slice(0, 10);
      if (query && !String(moment.content).toLocaleLowerCase("zh-CN").includes(query)) return false;
      if (ui.timeFilterType && moment.recordTypeId !== ui.timeFilterType) return false;
      if (ui.timeFilterTag && !(moment.tagIds || []).includes(ui.timeFilterTag)) return false;
      if (ui.timeFilterFrom && date < ui.timeFilterFrom) return false;
      if (ui.timeFilterTo && date > ui.timeFilterTo) return false;
      return true;
    });
  }

  function highlightedExcerpt(body, rawQuery) {
    const text = String(body || "");
    const query = String(rawQuery || "").trim();
    if (!query) return esc(text.length > 120 ? text.slice(0, 120) + "…" : text);
    const index = text.toLocaleLowerCase("zh-CN").indexOf(query.toLocaleLowerCase("zh-CN"));
    if (index < 0) return esc(text.length > 120 ? text.slice(0, 120) + "…" : text);
    const start = Math.max(0, index - 34);
    const end = Math.min(text.length, index + query.length + 70);
    return `${start ? "…" : ""}${esc(text.slice(start, index))}<mark>${esc(text.slice(index, index + query.length))}</mark>${esc(text.slice(index + query.length, end))}${end < text.length ? "…" : ""}`;
  }

  function searchMomentRowHtml(moment, selected) {
    const image = moment.images?.[0];
    return `<button class="search-result-row ${selected ? "is-selected" : ""}" data-action="workspace-open" data-id="${esc(moment.id)}">
      <span class="search-result-date"><strong>${esc(String(moment.occurrenceAt).slice(5, 10).replace("-", "/"))}</strong><small>${esc(String(moment.occurrenceAt).slice(0, 4))}</small></span>
      <span class="search-result-main"><span class="search-result-body">${highlightedExcerpt(moment.body, ui.timeQuery)}</span><span class="search-result-meta">${moment.type ? `<i>${esc(moment.type)}</i>` : ""}${(moment.tags || []).map((tag) => `<em>#${esc(tag)}</em>`).join("")}</span></span>
      ${image ? imageThumbHtml(image, "workspace-row-photo") : `<span class="workspace-row-next">${icon("next")}</span>`}
    </button>`;
  }

  function laterMomentsHtml(moment) {
    const later = sortMoments(getMoments().filter((item) => item.sourceMomentId === moment.id));
    if (!later.length) return "";
    return `<section class="moment-trail"><div><span>${icon("reply")}</span><small>后来从这里继续写下</small></div>${later.map((item) => `<button data-action="workspace-open-related" data-id="${esc(item.id)}"><time>${esc(formatSourceDate(item.occurrenceAt))}</time><strong>${esc(item.body.length > 64 ? item.body.slice(0, 64) + "…" : item.body)}</strong>${icon("next")}</button>`).join("")}</section>`;
  }

  function route() {
    const hashPath = location.hash.replace(/^#\/?/, "");
    const pathname = location.pathname.replace(/^\/+|\/+$/g, "");
    const raw = hashPath || pathname || (isAuthed() ? "today" : "login");
    const parts = raw.split("/").filter(Boolean);
    return { name: parts[0] || "today", id: parts[1] || "", mode: parts[2] || "" };
  }

  function go(path) {
    location.hash = `#/${path}`;
  }

  function isAuthed() {
    return cloud.configured ? Boolean(cloud.state.user) : true;
  }

  function icon(name) {
    const paths = {
      today: '<rect x="4" y="5" width="16" height="15" rx="4"/><path d="M8 3v4M16 3v4M4 10h16"/><path d="M8 14h.01M12 14h.01M16 14h.01"/>',
      time: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5v5l3.5 2"/>',
      memory: '<path d="M12 3.5c1.3 3.7 3.2 5.6 6.9 6.9-3.7 1.3-5.6 3.2-6.9 6.9-1.3-3.7-3.2-5.6-6.9-6.9C8.8 9.1 10.7 7.2 12 3.5Z"/><path d="M18.5 16.5c.5 1.4 1.3 2.2 2.7 2.7-1.4.5-2.2 1.3-2.7 2.7-.5-1.4-1.3-2.2-2.7-2.7 1.4-.5 2.2-1.3 2.7-2.7Z"/>',
      me: '<circle cx="12" cy="8" r="3.5"/><path d="M5.5 20c.7-4 3-6 6.5-6s5.8 2 6.5 6"/>',
      pen: '<path d="m5 19 1.1-4.2L16.8 4.1a2 2 0 0 1 2.8 2.8L8.9 17.6 5 19Z"/><path d="m14.8 6.1 3.1 3.1M6.2 14.7l3.1 3.1"/>',
      bookmark: '<path d="M7 4.5h10a1.5 1.5 0 0 1 1.5 1.5v14l-6.5-4-6.5 4V6A1.5 1.5 0 0 1 7 4.5Z"/>',
      expand: '<path d="M6 7h12M6 12h8M6 17h10"/><path d="M19 15v5M16.5 17.5h5"/>',
      check: '<path d="m6 12.5 3.6 3.6L18.5 7"/>',
      leaf: '<path d="M19.5 4.5C12 4.7 7.1 8 6.5 14.5c4.7.8 9.6-1.6 13-10Z"/><path d="M5 20c2.1-4.1 5.3-7 9.7-9"/>',
      moon: '<path d="M19.5 15.2A8.2 8.2 0 0 1 8.8 4.5 8.2 8.2 0 1 0 19.5 15.2Z"/>',
      clock: '<circle cx="12" cy="12" r="8"/><path d="M12 8v4.5l3 1.8"/>',
      calendar: '<rect x="4" y="5" width="16" height="15" rx="4"/><path d="M8 3v4M16 3v4M4 10h16"/>',
      image: '<rect x="3.5" y="5" width="17" height="14" rx="3"/><circle cx="9" cy="10" r="1.7"/><path d="m5.5 17 4.2-4 2.7 2.4 2.8-3 3.3 4.6"/>',
      timeline: '<path d="M7 5h12M7 12h12M7 19h12"/><circle cx="4" cy="5" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="19" r="1"/>',
      shuffle: '<path d="M4 7h2.5c4.5 0 6.5 10 11 10H20"/><path d="m17 14 3 3-3 3"/><path d="M4 17h2.5c1.4 0 2.6-.9 3.6-2.2M14 8.6c1-1 2.1-1.6 3.5-1.6H20"/><path d="m17 4 3 3-3 3"/>',
      reply: '<path d="M9 8 4 12l5 4"/><path d="M5 12h8c4 0 6 2.1 6 6"/>',
      back: '<path d="m15 18-6-6 6-6"/>',
      next: '<path d="m9 18 6-6-6-6"/>',
      plus: '<path d="M12 5v14M5 12h14"/>',
      edit: '<path d="m5 19 1-4 9.7-9.7a2 2 0 0 1 2.8 2.8L8.8 17.8 5 19Z"/>',
      trash: '<path d="M5 7h14M9 7V4.5h6V7M7 7l1 13h8l1-13M10 11v5M14 11v5"/>',
      hide: '<path d="M3.5 12s3.2-5 8.5-5 8.5 5 8.5 5-3.2 5-8.5 5-8.5-5-8.5-5Z"/><circle cx="12" cy="12" r="2.5"/><path d="m4 4 16 16"/>',
      show: '<path d="M3.5 12s3.2-5 8.5-5 8.5 5 8.5 5-3.2 5-8.5 5-8.5-5-8.5-5Z"/><circle cx="12" cy="12" r="2.5"/>',
      up: '<path d="m7 14 5-5 5 5"/>',
      down: '<path d="m7 10 5 5 5-5"/>',
      logout: '<path d="M10 5H5v14h5M14 8l4 4-4 4M8 12h10"/>',
      tag: '<path d="M4 5v6.5L12.5 20 20 12.5 11.5 4H5a1 1 0 0 0-1 1Z"/><circle cx="8" cy="8" r="1"/>',
      search: '<circle cx="10.5" cy="10.5" r="6.5"/><path d="m15.5 15.5 4 4"/>',
      filter: '<path d="M4 6h16M7 12h10M10 18h4"/>',
      download: '<path d="M12 4v11M8 11l4 4 4-4"/><path d="M5 19h14"/>',
      upload: '<path d="M12 16V5M8 9l4-4 4 4"/><path d="M5 19h14"/>',
      shield: '<path d="M12 3.5 19 6v5.2c0 4.3-2.6 7.3-7 9.3-4.4-2-7-5-7-9.3V6l7-2.5Z"/><path d="M9.5 12 11 13.5l3.5-4"/>',
    };
    return `<svg class="ui-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${paths[name] || paths.leaf}</svg>`;
  }

  function nav(active) {
    const items = [
      ["today", "今天", "today"], ["time", "时间", "time"], ["memory", "回忆", "memory"], ["me", "我的", "me"],
    ];
    const navItem = ([key, label, iconName]) => `<button class="nav-item ${active === key ? "active" : ""}" data-nav="${key}" aria-current="${active === key ? "page" : "false"}">${icon(iconName)}<span>${label}</span></button>`;
    return `<header class="site-header"><div class="header-inner">
      <button class="brand" data-action="brand-today" aria-label="返回今天">
        <span class="brand-mark">${icon("leaf")}</span>
        <span class="brand-copy"><strong>青屿漫记</strong><small>记录此刻，也在以后重新遇见</small></span>
      </button>
      <nav class="main-nav" aria-label="主要导航">${items.map(navItem).join("")}</nav>
      <button class="profile" data-nav="me" aria-label="个人空间"><span>${esc((cloud.state.user?.email || "屿").slice(0, 1).toUpperCase())}</span></button>
    </div></header>`;
  }

  function shell(content, active) {
    return `<div class="app-shell">${nav(active)}<main class="main-content">${content}</main></div>`;
  }

  function renderLogin() {
    const isRegister = ui.authMode === "register";
    const serviceMessage = cloud.configured ? "登录后，记录会安全保存到你的账号，并可在其他设备重新读取。" : "正式数据服务尚未配置。请先在 .env.local 填入 Supabase Project URL 与 Publishable key。";
    app.innerHTML = `<main class="auth-shell">
      <section class="auth-story" aria-labelledby="login-title">
        <div class="auth-brand"><span>${icon("leaf")}</span><strong>青屿漫记</strong></div>
        <p class="auth-eyebrow">个人生活采集与回顾工具</p>
        <h1 id="login-title">写下一句话，<br>把今天留住。</h1>
        <p class="auth-lead">想到什么就写什么，一句话也会被好好收下。等时间过去，它还会在某一天重新回到你面前。</p>
        <div class="auth-loop" aria-label="产品核心闭环">
          <span><i>1</i>写下一句</span><b></b><span><i>2</i>留在时间里</span><b></b><span><i>3</i>以后再遇见</span>
        </div>
      </section>
      <section class="auth-panel" aria-label="进入产品">
        <div class="auth-form-heading"><span class="auth-choice-icon">${icon(isRegister ? "leaf" : "pen")}</span><div><h2>${isRegister ? "创建个人账号" : "回到你的青屿"}</h2><p>${esc(serviceMessage)}</p></div></div>
        <form class="auth-form" id="auth-form">
          <label for="auth-email">邮箱</label>
          <input class="input" id="auth-email" name="email" type="email" autocomplete="email" placeholder="name@example.com" required ${cloud.configured ? "" : "disabled"} />
          <label for="auth-password">密码</label>
          <input class="input" id="auth-password" name="password" type="password" autocomplete="${isRegister ? "new-password" : "current-password"}" minlength="8" placeholder="至少 8 位" required ${cloud.configured ? "" : "disabled"} />
          ${ui.authError ? `<div class="inline-error" role="alert">${esc(ui.authError)}</div>` : ""}
          ${ui.authNotice ? `<div class="auth-success" role="status">${esc(ui.authNotice)}</div>` : ""}
          <button class="btn btn-primary auth-submit" type="submit" ${cloud.configured && !ui.authBusy ? "" : "disabled"}>${ui.authBusy ? "请稍候…" : isRegister ? "注册并进入" : "登录"}</button>
        </form>
        <button class="auth-switch" data-action="auth-switch">${isRegister ? "已有账号？登录" : "第一次来？创建账号"}</button>
        <p class="auth-note">你的记录只对当前账号可见。退出后，本机草稿仍会保留。</p>
      </section>
    </main>`;
  }

  function typeOptionsHtml(selected) {
    const available = getAvailableTypes();
    const selectedId = typeIdByName(selected) || selected || "";
    const options = [...available];
    const historical = getTypeItems().find((item) => item.id === selectedId);
    if (historical && !options.some((item) => item.id === historical.id)) options.unshift(historical);
    return options.map((item) => `<option value="${esc(item.id)}" ${selectedId === item.id ? "selected" : ""}>${esc(item.name)}${item.isArchived ? "（已停用）" : ""}</option>`).join("");
  }

  function momentEditorBodyHtml(data, target, momentId = "", placeholder = "写一句也可以……") {
    const config = {
      draft: ["draft-body", ""],
      global: ["global-draft-body", ""],
      edit: ["edit-body", `data-edit-id="${esc(momentId)}"`],
      workspace: ["workspace-edit-body", `data-workspace-id="${esc(momentId)}"`],
      reply: ["reply-body", `data-reply-source="${esc(momentId)}"`],
      "memory-reply": ["memory-reply-body", `data-reply-source="${esc(momentId)}"`],
    }[target] || [`${target}-body`, ""];
    return `<textarea id="${config[0]}" ${config[1]} placeholder="${esc(placeholder)}">${esc(data.content ?? data.body ?? "")}</textarea>`;
  }

  function composerHtml() {
    const draft = getDraft();
    const progressiveVisible = Boolean(draft.body.trim() || draft.expanded || draft.images.length || draft.type || draft.tags.length);
    const placeholder = TYPE_PROMPTS[draft.type] || "刚刚有什么，让你想记下来？";
    return `<section class="recorder composer" aria-label="快速记录">
      <div class="recorder-ripple" aria-hidden="true"><svg viewBox="0 0 1000 70" preserveAspectRatio="none"><path d="M0 46c118-21 204-20 308 0s193 20 290-2 202-21 402 4"/><path d="M0 58c114-15 218-14 321 1s204 14 305-2 211-14 374 2"/></svg></div>
      <div class="draft-status ${ui.draftSaveState === "saving" ? "is-saving" : ""}" data-draft-indicator ${progressiveVisible ? "" : "hidden"}><span class="draft-dot"></span>${draftSaveLabel()}</div>
      <label class="sr-only" for="draft-body">记录正文</label>
      ${momentEditorBodyHtml(draft, "draft", "", placeholder)}
      ${ui.inlineError ? `<div class="inline-error" role="alert">${esc(ui.inlineError)}</div>` : ""}
      ${ui.typeGuideOpen ? typeGuideHtml(draft.type, "draft") : ""}
      ${draft.expanded ? expandedFieldsHtml(draft, "draft") : ""}
      <div class="recorder-footer composer-actions">
        <div class="gentle-actions" data-progressive-actions ${progressiveVisible ? "" : "hidden"}>
          <button class="icon-action ${draft.expanded ? "is-open" : ""}" data-action="toggle-expand">${icon(draft.expanded ? "up" : "expand")}<span>${draft.expanded ? "收起补充" : "想多写一点"}</span></button>
          ${draft.expanded ? `<button class="icon-action ${ui.typeGuideOpen ? "is-open" : ""}" data-action="toggle-types">${icon("leaf")}<span>${draft.type || "选择记录类型"}</span></button><button class="icon-action" data-action="open-full-record">${icon("next")}<span>在完整页面写</span></button>` : ""}
        </div>
        <span class="composer-first-hint" ${progressiveVisible ? "hidden" : ""}>写一句，就可以保存 · Ctrl Enter</span>
        <div class="save-area"><span class="char-count">${draft.body.length} 字</span><button class="save-button" id="quick-save" data-action="save-moment" ${draft.body.trim() ? "" : "disabled"}>记下来</button></div>
      </div>
    </section>`;
  }

  function promptTypesHtml(selected) {
    const available = getAvailableTypes();
    const commonNames = ["小小幸福", "今天学到", "此刻感受", "突然想到", "生活瞬间"];
    const common = commonNames.map((name) => available.find((item) => item.name === name)).filter(Boolean);
    const visible = ui.promptTypesExpanded ? available : common;
    return `<section class="prompt-section" aria-labelledby="prompt-title">
      <div class="prompt-heading"><span class="prompt-icon">${icon("leaf")}</span><div><h2 id="prompt-title">你想记录什么？</h2><p>如果不知道从哪里开始，选一个轻提示；也可以直接跳过。</p></div></div>
      <div class="prompt-types">${visible.map((item) => `<button class="prompt-chip ${selected === item.name ? "is-selected" : ""}" data-action="choose-prompt-type" data-type="${esc(item.name)}">${esc(item.name)}</button>`).join("")}${available.length > common.length ? `<button class="prompt-more" data-action="toggle-prompt-types">${ui.promptTypesExpanded ? "收起" : "更多"}${icon(ui.promptTypesExpanded ? "up" : "next")}</button>` : ""}</div>
      ${selected ? `<p class="prompt-question">${esc(TYPE_PROMPTS[selected] || getTypeItems().find((item) => item.name === selected)?.description || "从这一类开始写，也可以随时换一种。")}</p>` : ""}
    </section>`;
  }

  function typeGuideHtml(selected, target) {
    return `<section class="guidance" aria-label="记录类型引导">
      <h3>${icon("bookmark")}<span>你想记录什么？</span></h3>
      <p>它只是在你想多说一点时提供提示，可以完全不选。</p>
      <div class="chip-grid">${getAvailableTypes().map((item) => `
        <button class="chip ${selected === item.name ? "is-selected" : ""}" data-action="choose-type" data-target="${target}" data-type="${esc(item.name)}">${esc(item.name)}</button>`).join("")}</div>
      <button class="btn btn-ghost btn-small" data-action="clear-type" data-target="${target}">不选择类型</button>
    </section>`;
  }

  function expandedFieldsHtml(data, target, momentId = "") {
    const momentAttr = momentId ? ` data-moment-id="${esc(momentId)}"` : "";
    const selectedTypeId = data.recordTypeId || typeIdByName(data.type);
    const selectedTagIds = Array.isArray(data.tagIds) ? data.tagIds : (data.tags || []).map((name) => tagIdByName(name)).filter(Boolean);
    const selectedTags = selectedTagIds.map((id) => getTagItems().find((item) => item.id === id)).filter(Boolean);
    return `<div class="expanded-fields">
      <div class="field">
        <span class="field-label">图片 <span class="field-hint">最多 3 张，${cloud.configured ? "保存时上传至私有空间" : "保存在当前浏览器"}</span></span>
        ${photoGridHtml(data.images, target, momentId)}
        <button class="btn btn-small" data-action="open-images" data-target="${target}"${momentAttr} ${data.images.length >= 3 ? "disabled" : ""}>${data.images.length >= 3 ? "已添加 3 / 3" : `添加图片 ${data.images.length} / 3`}</button>
      </div>
      <div class="field">
        <label class="field-label" for="${target}-record-type">记录类型 <span class="field-hint">可选，每条最多一个</span></label>
        <select class="input" id="${target}-record-type" data-editor-type="${target}"${momentAttr}><option value="">不选择</option>${typeOptionsHtml(selectedTypeId)}</select>
      </div>
      <div class="field">
        <span class="field-label">主题标签 <span class="field-hint">可添加多个，按 Enter 确认</span></span>
        <div class="tag-list">${selectedTags.map((tag) => `<span class="tag">${esc(tag.name)}<button aria-label="移除标签 ${esc(tag.name)}" data-action="remove-tag" data-target="${target}"${momentAttr} data-tag-id="${esc(tag.id)}">×</button></span>`).join("")}</div>
        <input class="input" id="${target}-tag-input" data-tag-input="${target}"${momentAttr} placeholder="例如：朋友、产品、周末" />
        <div class="chip-grid" style="margin-top:8px">${getTags().map((tag) => `<button class="chip ${selectedTagIds.includes(tag.id) ? "is-selected" : ""}" data-action="add-tag" data-target="${target}"${momentAttr} data-tag-id="${esc(tag.id)}" data-tag="${esc(tag.name)}">${esc(tag.name)}</button>`).join("")}</div>
      </div>
      <div class="field">
        <label class="field-label" for="${target}-occurrence">发生时间</label>
        <input class="input" id="${target}-occurrence" data-occurrence="${target}"${momentAttr} type="datetime-local" max="${nowLocalValue()}" value="${esc(data.occurredAt || data.occurrenceAt)}" />
        <span class="field-hint">日期按事情发生时确认的当地日期保存。创建时间不会被修改。</span>
      </div>
    </div>`;
  }

  function photoGridHtml(images, target, momentId = "") {
    if (!images.length) return "";
    return `<div class="photo-grid">${images.map((id) => {
      const label = imageLabel(id);
      const visual = String(id).startsWith("img:") ? `<img class="photo-visual local-photo" data-local-image="${esc(id)}" alt="${esc(label)}" /><span class="photo-missing-copy">图片暂时无法读取</span>` : `<div class="photo-visual photo-${esc(id)}"><span>${esc(label)}</span></div>`;
      return `<div class="photo"><button class="photo-open" data-action="open-image-viewer" data-image="${esc(id)}" aria-label="查看大图：${esc(label)}">${visual}</button>${target === "readonly" ? "" : `<button class="photo-remove" aria-label="删除图片 ${esc(label)}" data-action="remove-image" data-target="${target}" ${momentId ? `data-moment-id="${esc(momentId)}"` : ""} data-image="${esc(id)}">×</button>`}</div>`;
    }).join("")}</div>`;
  }

  function momentCardHtml(moment) {
    return `<button class="moment-card" data-moment-id="${moment.id}">
      <span class="moment-time">${icon("clock")}${esc(formatTime(moment.occurrenceAt))}</span>
      <span class="moment-body">${esc(moment.body.length > 130 ? moment.body.slice(0, 130) + "…" : moment.body)}</span>
      ${moment.images?.length ? `<span class="mini-photo-row">${moment.images.map((id) => imageThumbHtml(id, "mini-photo")).join("")}</span>` : ""}
      <span class="moment-meta">${moment.type ? `<span class="moment-type">${esc(moment.type)}</span>` : ""}${(moment.tags || []).map((tag) => `<span class="moment-tag">#${esc(tag)}</span>`).join("")}</span>
    </button>`;
  }

  function todayMomentHtml(moment, index) {
    const accents = ["sun", "leaf", "spark", "wave"];
    return `<article class="moment moment-${accents[index % accents.length]}">
      <button class="moment-open" data-moment-id="${esc(moment.id)}" aria-label="打开 ${esc(formatTime(moment.occurrenceAt))} 的记录">
        <span class="moment-dot" aria-hidden="true"></span>
        <time>${esc(formatTime(moment.occurrenceAt))}</time>
        <span class="moment-copy">${moment.type ? `<span class="moment-kind">${icon(moment.type === "小小幸福" ? "memory" : moment.type === "今天学到" ? "bookmark" : "leaf")}${esc(moment.type)}</span>` : ""}<span class="moment-text">${esc(moment.body)}</span></span>
        ${moment.images?.length ? imageThumbHtml(moment.images[0], "moment-photo") : ""}
      </button>
      ${(moment.tags || []).length ? `<div class="moment-tags">${moment.tags.map((tag) => `<span># ${esc(tag)}</span>`).join("")}</div>` : ""}
    </article>`;
  }

  function workspaceMomentRowHtml(moment, selected, showDate = false) {
    const image = moment.images?.[0];
    return `<button class="workspace-moment-row ${selected ? "is-selected" : ""}" data-action="workspace-open" data-id="${esc(moment.id)}">
      <span class="workspace-row-time">${showDate ? `<small>${esc(String(moment.occurrenceAt).slice(5, 10).replace("-", "/"))}</small>` : ""}${esc(formatTime(moment.occurrenceAt))}</span>
      <span class="workspace-row-main"><strong>${esc(moment.body.length > 74 ? moment.body.slice(0, 74) + "…" : moment.body)}</strong><span>${moment.type ? esc(moment.type) : "随手记下"}${(moment.tags || []).length ? ` · #${esc(moment.tags[0])}` : ""}</span></span>
      ${image ? imageThumbHtml(image, "workspace-row-photo") : `<span class="workspace-row-next">${icon("next")}</span>`}
    </button>`;
  }

  function workspaceInspectorHtml(moment, context) {
    if (!moment) return `<section class="workspace-inspector is-empty"><span>${icon("leaf")}</span><p><strong>选择一条记录</strong><small>在这里阅读和编辑，不需要离开当前工作区。</small></p></section>`;
    if (ui.workspaceEditing) {
      const data = getEditDraft(moment);
      return `<section class="workspace-inspector workspace-edit-pane">
        <header><div><small>编辑记录</small><strong>${esc(formatDateTime(moment.occurrenceAt))}</strong></div><button class="icon-btn" data-action="workspace-cancel-edit" data-id="${esc(moment.id)}" aria-label="退出编辑">×</button></header>
        <label class="field-label" for="workspace-edit-body">正文</label>${momentEditorBodyHtml(data, "workspace", moment.id)}
        ${expandedFieldsHtml(data, "workspace", moment.id)}
        <div class="form-actions"><button class="btn" data-action="workspace-cancel-edit" data-id="${esc(moment.id)}">取消</button><button class="btn btn-primary" data-action="workspace-save-edit" data-id="${esc(moment.id)}" data-context="${context}" ${data.body.trim() ? "" : "disabled"}>保存修改</button></div>
      </section>`;
    }
    const source = moment.sourceMomentId ? getMoments().find((item) => item.id === moment.sourceMomentId) : null;
    return `<article class="workspace-inspector workspace-reading-pane">
      <header><div><small>${esc(formatGroupDate(String(moment.occurrenceAt).slice(0, 10), true))}</small><strong>${esc(formatTime(moment.occurrenceAt))}</strong></div><span>${moment.type ? esc(moment.type) : "记录"}</span></header>
      <div class="workspace-reading-body">${esc(moment.body)}</div>
      ${photoGridHtml(moment.images || [], "readonly")}
      <div class="workspace-reading-meta">${(moment.tags || []).map((tag) => `<span># ${esc(tag)}</span>`).join("")}${moment.createdAt ? `<small>创建于 ${esc(formatDateTime(moment.createdAt))}</small>` : ""}</div>
      ${moment.sourceMomentId ? (source ? `<button class="workspace-source" data-action="workspace-open-source" data-id="${esc(source.id)}"><span>${icon("reply")}</span><p><small>来自 ${esc(formatSourceDate(source.occurrenceAt))}</small><strong>${esc(source.body.length > 54 ? source.body.slice(0, 54) + "…" : source.body)}</strong></p>${icon("next")}</button>` : `<div class="source-row source-missing">来源记录已删除，新记录仍然保留</div>`) : ""}
      ${laterMomentsHtml(moment)}
      <footer><button class="btn" data-action="workspace-edit" data-id="${esc(moment.id)}">编辑</button><button class="btn btn-danger" data-action="workspace-delete" data-id="${esc(moment.id)}" data-context="${context}">删除</button></footer>
    </article>`;
  }

  function renderToday() {
    const moments = sortMoments(getMoments().filter((m) => String(m.occurrenceAt).slice(0, 10) === todayKey()));
    const closure = getClosures()[todayKey()];
    let selected = moments.find((moment) => moment.id === ui.todaySelectedId) || moments[0] || null;
    ui.todaySelectedId = selected?.id || "";
    const body = `<section class="product-workspace today-workspace">
      <aside class="today-capture-pane">
        <header class="workspace-title"><div><h1>今天</h1><p>${esc(formatDateTitle())}</p></div><span class="sunlet">${icon("memory")}</span></header>
        <div class="capture-label"><strong>随手记下</strong><small>一句话即可保存</small></div>
        ${composerHtml()}
        ${moments.length ? `<button class="workspace-closure" data-action="open-closure"><span>${icon("moon")}</span><p><strong>${closure ? "今天已收束" : "给今天收个尾"}</strong><small>${closure ? esc(closure.finalLine || "查看今天留下的内容") : `${moments.length} 条记录，轻轻回看一下`}</small></p>${icon("next")}</button>` : ""}
      </aside>
      <section class="workspace-list-pane" aria-labelledby="today-list-title">
        <header><div><h2 id="today-list-title">今天的记录</h2><small>${moments.length} 条</small></div></header>
        <div class="workspace-moment-list">${moments.length ? moments.map((moment) => workspaceMomentRowHtml(moment, moment.id === selected?.id)).join("") : `<div class="workspace-list-empty"><span>${icon("pen")}</span><strong>今天还没有记录</strong><small>左侧写下一句话，它会直接出现在这里。</small></div>`}</div>
      </section>
      ${workspaceInspectorHtml(selected, "today")}
    </section>`;
    app.innerHTML = shell(body, "today");
    updateSaveButton();
  }

  function renderRecord() {
    const draft = getDraft();
    if (!draft.expanded) saveDraft({ expanded: true });
    const body = `<section class="record-page"><button class="btn btn-ghost detail-back" data-nav="today">← 返回今天</button>
      ${pageHeaderHtml("pen", "ONE MOMENT", "完整记录", "从一句话自然展开；正文仍是唯一必填项，其他信息都可以留空。")}
      <div class="record-sheet">${composerHtml()}</div>
      <aside class="record-principle"><span>${icon("leaf")}</span><p><strong>轻一点，也完整。</strong><small>图片、类型、标签与发生时间只帮助你补充语境，不会成为保存门槛。</small></p></aside>
    </section>`;
    app.innerHTML = shell(body, "today");
    updateSaveButton();
  }

  function pageHeaderHtml(iconName, eyebrow, title, subtitle) {
    return `<header class="page-header"><h1 class="page-title">${icon(iconName)}<span>${title}</span></h1><p class="page-subtitle">${subtitle}</p></header>`;
  }

  function historyMomentHtml(moment, returnPath = "") {
    return `<button class="history-moment" data-moment-id="${esc(moment.id)}" ${returnPath ? `data-return-path-direct="${esc(returnPath)}"` : ""}>
      <span class="history-time">${formatTime(moment.occurrenceAt)}</span>
      <span class="history-main"><span class="history-body">${esc(moment.body.length > 110 ? moment.body.slice(0, 110) + "…" : moment.body)}</span>
      <span class="moment-meta">${moment.type ? `<span class="moment-type">${esc(moment.type)}</span>` : ""}${(moment.tags || []).map((tag) => `<span class="moment-tag">#${esc(tag)}</span>`).join("")}</span></span>
      ${moment.images?.length ? imageThumbHtml(moment.images[0], "history-photo mini-photo") : ""}
    </button>`;
  }

  function timeGroupsHtml(moments) {
    const groups = new Map();
    sortMoments(moments).forEach((moment) => {
      const key = String(moment.occurrenceAt).slice(0, 10);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(moment);
    });
    return [...groups.entries()].map(([date, items]) => `<section class="date-group">
      <div class="date-group-heading"><h2>${esc(formatGroupDate(date, !date.startsWith(String(new Date().getFullYear()))))}</h2><span>${items.length} 条</span></div>
      <div class="history-list">${items.map(historyMomentHtml).join("")}</div>
    </section>`).join("");
  }

  function emptyTimelineHtml() {
    return `<section class="empty-timeline" aria-labelledby="empty-time-title">
      <div class="time-scaffold" aria-hidden="true">
        <div><span>今天</span><i></i></div>
        <div><span>昨天</span><i></i></div>
        <div><span>更早</span><i></i></div>
      </div>
      <div class="empty-timeline-copy"><h2 id="empty-time-title">时间会从第一条记录开始</h2><p>你留下的 Moment，会沿着这里慢慢长出来。</p><button class="btn btn-ghost btn-with-icon" data-nav="today">${icon("pen")}<span>去留下第一件小事</span></button></div>
    </section>`;
  }

  function monthKeyOffset(key, offset) {
    const [year, month] = key.split("-").map(Number);
    const date = new Date(year, month - 1 + offset, 1, 12);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  }

  function calendarHtml(moments) {
    const [year, month] = ui.calendarMonth.split("-").map(Number);
    const first = new Date(year, month - 1, 1, 12);
    const days = new Date(year, month, 0, 12).getDate();
    const offset = (first.getDay() + 6) % 7;
    const counts = moments.reduce((map, moment) => {
      const key = String(moment.occurrenceAt).slice(0, 10);
      map[key] = (map[key] || 0) + 1;
      return map;
    }, {});
    if (!ui.selectedDate.startsWith(ui.calendarMonth)) {
      ui.selectedDate = Object.keys(counts).filter((key) => key.startsWith(ui.calendarMonth)).sort().at(-1) || `${ui.calendarMonth}-01`;
    }
    const blanks = Array.from({ length: offset }, () => '<span class="calendar-blank"></span>').join("");
    const cells = Array.from({ length: days }, (_, index) => {
      const day = index + 1;
      const key = `${ui.calendarMonth}-${String(day).padStart(2, "0")}`;
      const count = counts[key] || 0;
      return `<button class="calendar-day ${count ? "has-moments" : ""} ${ui.selectedDate === key ? "is-selected" : ""}" data-action="select-date" data-date="${key}" aria-label="${year}年${month}月${day}日${count ? `，${count}条记录` : ""}"><span>${day}</span>${count ? '<i aria-hidden="true"></i>' : ""}</button>`;
    }).join("");
    const selected = sortMoments(moments.filter((moment) => String(moment.occurrenceAt).slice(0, 10) === ui.selectedDate));
    return `<section class="calendar-panel">
      <div class="calendar-toolbar"><button class="icon-btn" data-action="month-prev" aria-label="上一个月">${icon("back")}</button><strong>${year}年${month}月</strong><button class="icon-btn" data-action="month-next" aria-label="下一个月">${icon("next")}</button></div>
      <div class="calendar-weekdays">${["一", "二", "三", "四", "五", "六", "日"].map((day) => `<span>${day}</span>`).join("")}</div>
      <div class="calendar-grid">${blanks}${cells}</div>
    </section>
    <section class="selected-day"><div class="date-group-heading"><h2>${esc(formatGroupDate(ui.selectedDate, true))}</h2><span>${selected.length} 条</span><button class="btn btn-small" data-action="open-date" data-date="${esc(ui.selectedDate)}">查看这一天</button></div>
      ${selected.length ? `<div class="history-list">${selected.map(historyMomentHtml).join("")}</div>` : '<div class="soft-empty">这一天没有 Moment。空白日期只是一段正常生活。</div>'}
    </section>`;
  }

  function timeDateNavigatorHtml(moments) {
    const counts = new Map();
    sortMoments(moments).forEach((moment) => {
      const key = String(moment.occurrenceAt).slice(0, 10);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return `<div class="time-date-nav"><button class="time-date-item ${ui.timeSelectedDate === "all" ? "is-selected" : ""}" data-action="time-date-select" data-date="all"><span><strong>全部记录</strong><small>连续浏览所有内容</small></span><b>${moments.length}</b></button>${[...counts.entries()].map(([date, count]) => `<button class="time-date-item ${ui.timeSelectedDate === date ? "is-selected" : ""}" data-action="time-date-select" data-date="${esc(date)}"><span><strong>${esc(formatGroupDate(date, !date.startsWith(String(new Date().getFullYear()))))}</strong><small>${date}</small></span><b>${count}</b></button>`).join("")}</div>`;
  }

  function timeCalendarNavigatorHtml(moments) {
    const [year, month] = ui.calendarMonth.split("-").map(Number);
    const first = new Date(year, month - 1, 1, 12);
    const days = new Date(year, month, 0, 12).getDate();
    const offset = (first.getDay() + 6) % 7;
    const counts = moments.reduce((map, moment) => {
      const key = String(moment.occurrenceAt).slice(0, 10);
      map[key] = (map[key] || 0) + 1;
      return map;
    }, {});
    const blanks = Array.from({ length: offset }, () => '<span class="calendar-blank"></span>').join("");
    const cells = Array.from({ length: days }, (_, index) => {
      const day = index + 1;
      const key = `${ui.calendarMonth}-${String(day).padStart(2, "0")}`;
      const count = counts[key] || 0;
      return `<button class="calendar-day ${count ? "has-moments" : ""} ${ui.timeSelectedDate === key ? "is-selected" : ""}" data-action="time-date-select" data-date="${key}" aria-label="${year}年${month}月${day}日${count ? `，${count}条记录` : ""}"><span>${day}</span>${count ? '<i aria-hidden="true"></i>' : ""}</button>`;
    }).join("");
    return `<section class="workspace-calendar"><div class="calendar-toolbar"><button class="icon-btn" data-action="month-prev" aria-label="上一个月">${icon("back")}</button><strong>${year}年${month}月</strong><button class="icon-btn" data-action="month-next" aria-label="下一个月">${icon("next")}</button></div><div class="calendar-weekdays">${["一", "二", "三", "四", "五", "六", "日"].map((day) => `<span>${day}</span>`).join("")}</div><div class="calendar-grid">${blanks}${cells}</div><button class="time-all-link" data-action="time-date-select" data-date="all">查看全部记录</button></section>`;
  }

  function timeSearchToolsHtml() {
    const filterCount = [ui.timeFilterType, ui.timeFilterTag, ui.timeFilterFrom || ui.timeFilterTo].filter(Boolean).length;
    return `<div class="time-tools"><label class="time-search" for="time-search">${icon("search")}<input id="time-search" type="search" value="${esc(ui.timeQuery)}" placeholder="搜索记录正文" autocomplete="off" /><kbd>Ctrl K</kbd></label><button class="time-filter-toggle ${filterCount ? "has-filter" : ""}" data-action="toggle-time-filters">${icon("filter")}<span>筛选</span>${filterCount ? `<b>${filterCount}</b>` : ""}</button></div>`;
  }

  function closureViewHtml(closure, extraClass = "") {
    if (!closure) return "";
    const answers = Array.isArray(closure.answers) ? closure.answers.map((answer) => String(answer || "").trim()).filter(Boolean) : [];
    return `<section class="historical-closure ${extraClass}"><span>${icon("moon")}</span><div><small>这一天的收束</small>${answers.map((answer, index) => `<p><em>${esc(DAILY_QUESTIONS[index] || "当时留下")}</em>${esc(answer)}</p>`).join("")}${closure.finalLine ? `<p class="closure-final-line">${esc(closure.finalLine)}</p>` : ""}</div></section>`;
  }

  function timeFilterPanelHtml() {
    if (!ui.timeFilterOpen) return "";
    const momentTypeIds = [...new Set(getMoments().map((moment) => moment.recordTypeId).filter(Boolean))];
    const momentTagIds = [...new Set(getMoments().flatMap((moment) => moment.tagIds || []))];
    const momentTypes = getTypeItems().filter((item) => momentTypeIds.includes(item.id)).sort((a, b) => a.name.localeCompare(b.name, "zh-CN"));
    const momentTags = getTagItems().filter((item) => momentTagIds.includes(item.id)).sort((a, b) => a.name.localeCompare(b.name, "zh-CN"));
    return `<section class="time-filter-panel" aria-label="筛选记录">
      <label><span>记录类型</span><select class="input" id="time-filter-type"><option value="">全部类型</option>${momentTypes.map((type) => `<option value="${esc(type.id)}" ${type.id === ui.timeFilterType ? "selected" : ""}>${esc(type.name)}${type.isArchived ? "（已停用）" : ""}</option>`).join("")}</select></label>
      <label><span>主题标签</span><select class="input" id="time-filter-tag"><option value="">全部标签</option>${momentTags.map((tag) => `<option value="${esc(tag.id)}" ${tag.id === ui.timeFilterTag ? "selected" : ""}>${esc(tag.name)}${tag.isArchived ? "（已停用）" : ""}</option>`).join("")}</select></label>
      <label><span>开始日期</span><input class="input" id="time-filter-from" type="date" value="${esc(ui.timeFilterFrom)}" /></label>
      <label><span>结束日期</span><input class="input" id="time-filter-to" type="date" value="${esc(ui.timeFilterTo)}" /></label>
      <button class="btn btn-ghost btn-small" data-action="clear-time-filters" ${timeFiltersActive() ? "" : "disabled"}>清除条件</button>
    </section>`;
  }

  function renderTime() {
    const moments = sortMoments(getMoments());
    if (!moments.length) {
      app.innerHTML = shell(`<section class="product-workspace empty-workspace"><header class="workspace-topbar"><h1>时间</h1></header>${emptyTimelineHtml()}</section>`, "time");
      return;
    }
    const matchedMoments = filteredTimeMoments(moments);
    const activeSearch = timeFiltersActive();
    const visibleMoments = ui.timeSelectedDate === "all" ? matchedMoments : matchedMoments.filter((moment) => String(moment.occurrenceAt).slice(0, 10) === ui.timeSelectedDate);
    let selected = visibleMoments.find((moment) => moment.id === ui.workspaceMomentId) || visibleMoments[0] || null;
    ui.workspaceMomentId = selected?.id || "";
    const listTitle = activeSearch ? `找到 ${visibleMoments.length} 条记录` : ui.timeSelectedDate === "all" ? "全部记录" : formatGroupDate(ui.timeSelectedDate, true);
    const selectedClosure = ui.timeSelectedDate === "all" ? null : getClosures()[ui.timeSelectedDate];
    const closureHtml = closureViewHtml(selectedClosure, "time-inline-closure");
    const body = `<section class="product-workspace time-workspace">
      <header class="workspace-topbar time-topbar"><div><h1>时间</h1><small>${moments.length} 条记录</small></div>${timeSearchToolsHtml()}<div class="view-switch" role="group" aria-label="时间视图"><button class="${ui.timeView === "timeline" ? "is-active" : ""}" data-action="time-view" data-view="timeline">${icon("timeline")}<span>时间线</span></button><button class="${ui.timeView === "calendar" ? "is-active" : ""}" data-action="time-view" data-view="calendar">${icon("calendar")}<span>日历</span></button></div></header>
      ${timeFilterPanelHtml()}
      <div class="time-workspace-grid">
        <aside class="time-navigator">${ui.timeView === "timeline" ? timeDateNavigatorHtml(matchedMoments) : timeCalendarNavigatorHtml(matchedMoments)}</aside>
        <section class="workspace-list-pane"><header><div><h2>${esc(listTitle)}</h2><small>${visibleMoments.length} 条</small></div></header>${closureHtml}<div class="workspace-moment-list">${visibleMoments.length ? visibleMoments.map((moment) => activeSearch ? searchMomentRowHtml(moment, moment.id === selected?.id) : workspaceMomentRowHtml(moment, moment.id === selected?.id, ui.timeSelectedDate === "all")).join("") : `<div class="workspace-list-empty"><span>${icon(activeSearch ? "search" : "calendar")}</span><strong>${activeSearch ? "没有找到相符的记录" : "这一天没有记录"}</strong><small>${activeSearch ? "试试更短的关键词，或清除一个筛选条件。" : "空白日期只是一段正常生活。"}</small>${activeSearch ? '<button class="btn btn-ghost btn-small" data-action="clear-time-filters">一键清除筛选</button>' : ""}</div>`}</div></section>
        ${workspaceInspectorHtml(selected, "time")}
      </div>
    </section>`;
    app.innerHTML = shell(body, "time");
  }

  function renderDate(dateKey) {
    const moments = sortMoments(getMoments().filter((moment) => String(moment.occurrenceAt).slice(0, 10) === dateKey));
    const closure = getClosures()[dateKey];
    const fromMemory = ui.dateReturn === "memory";
    const body = `<section class="date-page"><button class="btn btn-ghost detail-back" data-nav="${fromMemory ? "memory" : "time"}">← ${fromMemory ? "返回回忆" : "返回日历"}</button>
      ${pageHeaderHtml("calendar", "A DAY IN TIME", formatGroupDate(dateKey, true), "这一天留下的 Moment 会一直在这里。")}
      ${moments.length ? `<div class="date-dayline"><div class="history-list">${moments.map((moment) => historyMomentHtml(moment, `date/${dateKey}`)).join("")}</div></div>` : '<div class="soft-empty date-empty"><strong>这一天没有 Moment。</strong><span>空白日期只是一段正常生活，不代表漏记或中断。</span></div>'}
      ${closureViewHtml(closure)}
    </section>`;
    app.innerHTML = shell(body, fromMemory ? "memory" : "time");
  }

  function memoryCandidatePool() {
    const historical = sortMoments(getMoments().filter((moment) => String(moment.occurredAt).slice(0, 10) < todayKey()));
    if (ui.memoryMode === "type") return ui.memoryType ? historical.filter((moment) => moment.recordTypeId === ui.memoryType) : [];
    if (ui.memoryMode === "todayish") {
      const today = parseLocalDate(todayKey());
      const exact = historical.filter((moment) => {
        const then = new Date(moment.occurredAt);
        return then.getMonth() === today.getMonth() && then.getDate() === today.getDate();
      });
      if (exact.length) return exact;
      return historical.filter((moment) => {
        const then = new Date(moment.occurredAt);
        const monthDistance = Math.abs((today.getFullYear() - then.getFullYear()) * 12 + today.getMonth() - then.getMonth());
        return monthDistance >= 1 && Math.abs(today.getDate() - then.getDate()) <= 3;
      });
    }
    return historical;
  }

  function memoryControlsHtml() {
    const historical = getMoments().filter((moment) => String(moment.occurredAt).slice(0, 10) < todayKey());
    const counts = historical.reduce((map, moment) => {
      if (moment.recordTypeId) map.set(moment.recordTypeId, (map.get(moment.recordTypeId) || 0) + 1);
      return map;
    }, new Map());
    const preferred = ["type:happiness", "type:learned", "type:thought"];
    const types = [...counts.keys()].sort((a, b) => (preferred.indexOf(a) < 0 ? 99 : preferred.indexOf(a)) - (preferred.indexOf(b) < 0 ? 99 : preferred.indexOf(b))).slice(0, 6);
    return `<div class="memory-controls"><div class="memory-mode-switch" role="group" aria-label="回忆方式"><button class="${ui.memoryMode === "random" ? "is-active" : ""}" data-action="memory-mode" data-mode="random">${icon("shuffle")}<span>随机捡回来</span></button><button class="${ui.memoryMode === "todayish" ? "is-active" : ""}" data-action="memory-mode" data-mode="todayish">${icon("calendar")}<span>过去的今天</span></button><button class="${ui.memoryMode === "type" ? "is-active" : ""}" data-action="memory-mode" data-mode="type">${icon("bookmark")}<span>按类型</span></button></div>${ui.memoryMode === "type" ? `<div class="memory-type-picks">${types.length ? types.map((typeId) => `<button class="${ui.memoryType === typeId ? "is-active" : ""}" data-action="memory-type" data-type="${esc(typeId)}">${esc(recordTypeName(typeId))}<small>${counts.get(typeId)}</small></button>`).join("") : '<span>有类型的旧记录积累后，会出现在这里。</span>'}</div>` : ""}</div>`;
  }

  function chooseMemoryCandidate(force = false) {
    const candidates = memoryCandidatePool();
    if (!candidates.length) { ui.memoryCurrentId = ""; return null; }
    if (!force && ui.memoryCurrentId) {
      const current = candidates.find((moment) => moment.id === ui.memoryCurrentId);
      if (current) return current;
    }
    let pool = candidates.filter((moment) => !ui.memorySeen.has(moment.id));
    if (!pool.length) { ui.memorySeen.clear(); pool = [...candidates]; }
    const shown = read(STORAGE.memoryShown, {});
    const cutoff = Date.now() - 30 * 86400000;
    const preferred = pool.filter((moment) => !shown[moment.id] || new Date(shown[moment.id]).getTime() < cutoff);
    if (preferred.length) pool = preferred;
    const selected = pool[Math.floor(Math.random() * pool.length)];
    ui.memoryCurrentId = selected.id;
    ui.memorySeen.add(selected.id);
    shown[selected.id] = new Date().toISOString();
    write(STORAGE.memoryShown, shown);
    return selected;
  }

  function emptyMemoryHtml() {
    return `<section class="memory-empty">
      <div class="memory-vessel">
        <span class="memory-spark memory-spark-one">${icon("leaf")}</span><span class="memory-spark memory-spark-two">${icon("memory")}</span>
        <span class="memory-paper-icon">${icon("bookmark")}</span>
        <p class="memory-waiting">等待第一段过去</p>
        <h2>偶尔，捡回来一点过去。</h2>
        <p>这里以后会出现一条来自过去的记录。有些当时普通的小事，过一阵子再遇见，会变得不一样。</p>
      </div>
      <div class="memory-how"><h3>它是怎么来的？</h3><p>你只需要慢慢留下生活，过去的记录以后会偶尔回到这里。</p></div>
      <button class="btn btn-ghost btn-with-icon memory-empty-action" data-nav="today">${icon("pen")}<span>去留下今天</span></button>
    </section>`;
  }

  function memorySideContentHtml(moment, sameDay, replyDraft, linked, memoryEdit) {
    if (ui.workspaceEditing) return `<section class="memory-side-section memory-side-edit"><header><button class="btn btn-ghost btn-small" data-action="workspace-cancel-edit" data-id="${esc(moment.id)}">← 返回</button><strong>编辑记录</strong></header><label class="field-label" for="workspace-edit-body">正文</label>${momentEditorBodyHtml(memoryEdit, "workspace", moment.id)}${expandedFieldsHtml(memoryEdit, "workspace", moment.id)}<div class="form-actions"><button class="btn" data-action="workspace-cancel-edit" data-id="${esc(moment.id)}">取消</button><button class="btn btn-primary" data-action="workspace-save-edit" data-id="${esc(moment.id)}" data-context="memory" ${memoryEdit.content.trim() ? "" : "disabled"}>保存修改</button></div></section>`;
    if (ui.memoryReplyOpen) return `<section class="memory-side-section memory-inline-reply"><header><button class="btn btn-ghost btn-small" data-action="memory-close-panel">← 返回</button><strong>写下现在想到的</strong></header><p class="memory-source-line">回应 ${esc(formatSourceDate(moment.occurredAt))} 的这条记录</p>${momentEditorBodyHtml(replyDraft, "memory-reply", moment.id, "此刻想到什么，就写一句……")}<div class="reply-gentle-row"><button class="btn btn-ghost btn-small btn-with-icon" data-action="toggle-memory-reply-extras" data-id="${esc(moment.id)}">${icon(replyDraft.expanded ? "up" : "expand")}<span>${replyDraft.expanded ? "收起补充" : "补充图片、类型、标签或时间"}</span></button><span>全部可选</span></div>${replyDraft.expanded ? expandedFieldsHtml(replyDraft, "reply", moment.id) : ""}<button class="btn btn-primary" id="memory-reply-save" data-action="save-memory-reply" data-id="${esc(moment.id)}" ${replyDraft.content.trim() ? "" : "disabled"}>保存为新记录</button></section>`;
    if (ui.memoryDayOpen) return `<section class="memory-side-section"><header><button class="btn btn-ghost btn-small" data-action="memory-close-panel">← 返回</button><strong>${esc(formatGroupDate(String(moment.occurredAt).slice(0, 10), true))}</strong></header><div class="memory-day-list">${sameDay.map((item) => `<button class="${item.id === moment.id ? "is-current" : ""}" data-action="memory-day-moment" data-id="${esc(item.id)}"><time>${esc(formatTime(item.occurredAt))}</time><span>${esc(item.content.length > 52 ? item.content.slice(0, 52) + "…" : item.content)}</span></button>`).join("")}</div></section>`;
    if (ui.memoryMetaOpen) return `<section class="memory-side-section"><header><button class="btn btn-ghost btn-small" data-action="memory-close-panel">← 返回</button><strong>完整记录</strong></header><dl class="memory-full-meta"><div><dt>发生时间</dt><dd>${esc(formatDateTime(moment.occurredAt))}</dd></div><div><dt>创建时间</dt><dd>${esc(formatDateTime(moment.createdAt))}</dd></div><div><dt>记录类型</dt><dd>${momentTypeName(moment) ? esc(momentTypeName(moment)) : "未选择"}</dd></div><div><dt>主题标签</dt><dd>${momentTagNames(moment).length ? momentTagNames(moment).map((tag) => `#${esc(tag)}`).join("　") : "未添加"}</dd></div></dl><button class="btn btn-small" data-action="workspace-edit" data-id="${esc(moment.id)}">编辑这条记录</button></section>`;
    return `<section class="memory-side-section memory-actions-panel"><div class="memory-side-date"><small>${esc(memoryTimeSense(moment.occurredAt))}</small><strong>${esc(formatMemoryDate(moment.occurredAt))}</strong></div><div class="memory-work-actions"><button data-action="memory-toggle-meta">${icon("bookmark")}<span><strong>查看完整记录</strong><small>时间、类型与标签</small></span>${icon("next")}</button><button data-action="memory-toggle-day">${icon("calendar")}<span><strong>看看那一天</strong><small>${sameDay.length} 条记录</small></span>${icon("next")}</button><button data-action="memory-toggle-reply">${icon("reply")}<span><strong>写下现在想到的</strong><small>创建一条新的关联记录</small></span>${icon("next")}</button><button data-action="next-memory">${icon("shuffle")}<span><strong>再捡一条</strong><small>换一段过去</small></span>${icon("next")}</button></div>${linked ? `<div class="memory-linked-result"><span>${icon("check")}</span><p><small>刚刚写下</small><strong>${esc(linked.content)}</strong></p></div>` : ""}</section>`;
  }

  function renderMemory() {
    const moment = chooseMemoryCandidate(false);
    if (!moment) {
      const emptyCopy = ui.memoryMode === "todayish" ? `<section class="memory-filter-empty"><span>${icon("calendar")}</span><h2>今天还没有相近日期的旧记录</h2><p>时间吻合时，它会自然出现。你也可以先随机捡回一条。</p><button class="btn btn-ghost" data-action="memory-mode" data-mode="random">随机捡一条</button></section>` : ui.memoryMode === "type" ? `<section class="memory-filter-empty"><span>${icon("bookmark")}</span><h2>${ui.memoryType ? `还没有“${esc(recordTypeName(ui.memoryType))}”的旧记录` : "选择一种记录类型"}</h2><p>只会从真实存在的过去内容中寻找。</p></section>` : emptyMemoryHtml();
      app.innerHTML = shell(`<section class="product-workspace memory-workspace"><header class="workspace-topbar"><div><h1>回忆</h1><small>让一段过去重新参与今天</small></div></header>${memoryControlsHtml()}${emptyCopy}</section>`, "memory");
      return;
    }
    const dateKey = String(moment.occurrenceAt).slice(0, 10);
    const sameDay = sortMoments(getMoments().filter((item) => String(item.occurrenceAt).slice(0, 10) === dateKey));
    const replyDraft = getReplyDraft(moment.id);
    const linked = ui.memoryLinkedMomentId ? getMoments().find((item) => item.id === ui.memoryLinkedMomentId) : null;
    const memoryEdit = getEditDraft(moment);
    const canonicalSideContent = memorySideContentHtml(moment, sameDay, replyDraft, linked, memoryEdit);
    if (false) {
    const sideContent = ui.workspaceEditing ? `<section class="memory-side-section memory-side-edit"><header><button class="btn btn-ghost btn-small" data-action="workspace-cancel-edit" data-id="${esc(moment.id)}">← 返回</button><strong>编辑记录</strong></header><label class="field-label" for="workspace-edit-body">正文</label><textarea id="workspace-edit-body" data-workspace-id="${esc(moment.id)}">${esc(memoryEdit.body)}</textarea><div class="field"><label class="field-label" for="workspace-edit-type">记录类型</label><select class="input" id="workspace-edit-type" data-workspace-type="${esc(moment.id)}"><option value="">不选择</option>${typeOptionsHtml(memoryEdit.type)}</select></div><div class="form-actions"><button class="btn" data-action="workspace-cancel-edit" data-id="${esc(moment.id)}">取消</button><button class="btn btn-primary" data-action="workspace-save-edit" data-id="${esc(moment.id)}" data-context="memory" ${memoryEdit.body.trim() ? "" : "disabled"}>保存修改</button></div></section>` : ui.memoryReplyOpen ? `<section class="memory-side-section memory-inline-reply"><header><button class="btn btn-ghost btn-small" data-action="memory-close-panel">← 返回</button><strong>写下现在想到的</strong></header><p class="memory-source-line">回应 ${esc(formatSourceDate(moment.occurrenceAt))} 的这条记录</p><textarea id="memory-reply-body" data-reply-source="${esc(moment.id)}" placeholder="此刻想到什么，就写一句……">${esc(replyDraft.body)}</textarea><button class="btn btn-primary" id="memory-reply-save" data-action="save-memory-reply" data-id="${esc(moment.id)}" ${replyDraft.body.trim() ? "" : "disabled"}>保存为新记录</button></section>` : ui.memoryDayOpen ? `<section class="memory-side-section"><header><button class="btn btn-ghost btn-small" data-action="memory-close-panel">← 返回</button><strong>${esc(formatGroupDate(dateKey, true))}</strong></header><div class="memory-day-list">${sameDay.map((item) => `<button class="${item.id === moment.id ? "is-current" : ""}" data-action="memory-day-moment" data-id="${esc(item.id)}"><time>${esc(formatTime(item.occurrenceAt))}</time><span>${esc(item.body.length > 52 ? item.body.slice(0, 52) + "…" : item.body)}</span></button>`).join("")}</div></section>` : ui.memoryMetaOpen ? `<section class="memory-side-section"><header><button class="btn btn-ghost btn-small" data-action="memory-close-panel">← 返回</button><strong>完整记录</strong></header><dl class="memory-full-meta"><div><dt>发生时间</dt><dd>${esc(formatDateTime(moment.occurrenceAt))}</dd></div><div><dt>创建时间</dt><dd>${esc(formatDateTime(moment.createdAt))}</dd></div><div><dt>记录类型</dt><dd>${moment.type ? esc(moment.type) : "未选择"}</dd></div><div><dt>主题标签</dt><dd>${moment.tags?.length ? moment.tags.map((tag) => `#${esc(tag)}`).join("　") : "未添加"}</dd></div></dl><button class="btn btn-small" data-action="workspace-edit" data-id="${esc(moment.id)}">编辑这条记录</button></section>` : `<section class="memory-side-section memory-actions-panel"><div class="memory-side-date"><small>${esc(memoryTimeSense(moment.occurrenceAt))}</small><strong>${esc(formatMemoryDate(moment.occurrenceAt))}</strong></div><div class="memory-work-actions"><button data-action="memory-toggle-meta">${icon("bookmark")}<span><strong>查看完整记录</strong><small>时间、类型与标签</small></span>${icon("next")}</button><button data-action="memory-toggle-day">${icon("calendar")}<span><strong>看看那一天</strong><small>${sameDay.length} 条记录</small></span>${icon("next")}</button><button data-action="memory-toggle-reply">${icon("reply")}<span><strong>写下现在想到的</strong><small>创建一条新的关联记录</small></span>${icon("next")}</button><button data-action="next-memory">${icon("shuffle")}<span><strong>再捡一条</strong><small>换一段过去</small></span>${icon("next")}</button></div>${linked ? `<div class="memory-linked-result"><span>${icon("check")}</span><p><small>刚刚写下</small><strong>${esc(linked.body)}</strong></p></div>` : ""}</section>`;
    }
    const typeName = momentTypeName(moment);
    const tagNames = momentTagNames(moment);
    const memoryTypeLabel = ui.memoryMode === "type" ? recordTypeName(ui.memoryType) : "";
    const body = `<section class="product-workspace memory-workspace"><header class="workspace-topbar"><div><h1>回忆</h1><small>${ui.memoryMode === "todayish" ? "从相近的日期里重新遇见" : ui.memoryMode === "type" ? `以前的${esc(memoryTypeLabel)}` : "今天捡回的一条记录"}</small></div></header>${memoryControlsHtml()}<div class="memory-workspace-grid"><article class="memory-document"><header><div><strong>${esc(memoryTimeSense(moment.occurredAt))}</strong><time>${esc(formatMemoryDate(moment.occurredAt))} · ${esc(formatTime(moment.occurredAt))}</time></div><span>${icon("memory")}</span></header><div class="memory-document-body">${esc(moment.content)}</div>${photoGridHtml(moment.images || [], "readonly")}<footer>${typeName ? `<span class="moment-type">${esc(typeName)}</span>` : ""}${tagNames.map((tag) => `<span class="moment-tag">#${esc(tag)}</span>`).join("")}</footer></article><aside class="memory-context-pane">${canonicalSideContent}</aside></div></section>`;
    app.innerHTML = shell(body, "memory");
  }

  function emptyReplyDraft(sourceId) {
    return withEditorAliases({ ...emptyDraft(), sourceId, sourceMomentId: sourceId });
  }

  function getReplyDraft(sourceId) {
    const draft = read(STORAGE.replyDraft, null);
    return draft?.sourceId === sourceId || draft?.sourceMomentId === sourceId
      ? withEditorAliases({ ...emptyReplyDraft(sourceId), ...canonicalEditorData(draft, sourceId), sourceId })
      : emptyReplyDraft(sourceId);
  }

  function saveReplyDraft(sourceId, patch) {
    const next = withEditorAliases({ ...canonicalEditorData({ ...getReplyDraft(sourceId), ...patch }, sourceId), sourceId, updatedAt: Date.now() });
    write(STORAGE.replyDraft, next);
    const button = document.getElementById("reply-save");
    if (button) button.disabled = !next.body.trim();
    return next;
  }

  function renderReply(sourceId) {
    const source = getMoments().find((moment) => moment.id === sourceId);
    if (!source) {
      app.innerHTML = shell(`<section class="detail"><button class="btn btn-ghost detail-back" data-nav="memory">← 返回回忆</button><h1 class="page-title">来源记录已删除</h1><p class="page-subtitle">无法继续建立来源关系。</p></section>`, "memory");
      return;
    }
    const draft = getReplyDraft(sourceId);
    const extrasVisible = ui.replyExtrasOpen || draft.type || draft.tags.length;
    const body = `<section class="reply-page">
      <button class="btn btn-ghost detail-back" data-nav="memory">← 返回回忆</button>
      ${pageHeaderHtml("reply", "NOW · RESPOND", "写下现在想到的", "你现在写下的会成为一条新记录，并和这段过去相连。")}
      <div class="reply-source"><span>${icon("memory")}</span><div><small>${esc(memoryTimeSense(source.occurrenceAt))} · ${esc(formatMemoryDate(source.occurrenceAt))}</small><p>${esc(source.body.length > 110 ? source.body.slice(0, 110) + "…" : source.body)}</p></div></div>
      <section class="reply-composer"><label for="reply-body">看到它以后，此刻想到了什么？</label>${momentEditorBodyHtml(draft, "reply", sourceId, "写一句现在的感受……")}
        <div class="reply-gentle-row"><button class="btn btn-ghost btn-small btn-with-icon" data-action="toggle-reply-extras">${icon(extrasVisible ? "up" : "expand")}<span>${extrasVisible ? "收起补充" : "补充类型或标签"}</span></button><span>这些都可以不填</span></div>
        ${extrasVisible ? `<div class="reply-extras">${expandedFieldsHtml(draft, "reply", sourceId)}</div>` : ""}
        <div class="form-actions"><button class="btn btn-primary btn-with-icon" id="reply-save" data-action="save-reply" data-id="${esc(sourceId)}" ${draft.body.trim() ? "" : "disabled"}>${icon("check")}<span>把现在的想法记下来</span></button></div>
      </section>
    </section>`;
    app.innerHTML = shell(body, "memory");
  }

  function typeManageRow(item, index, total) {
    const reorder = `<button class="icon-btn" data-action="move-type" data-id="${esc(item.id)}" data-direction="up" aria-label="上移 ${esc(item.name)}" ${index === 0 ? "disabled" : ""}>${icon("up")}</button><button class="icon-btn" data-action="move-type" data-id="${esc(item.id)}" data-direction="down" aria-label="下移 ${esc(item.name)}" ${index === total - 1 ? "disabled" : ""}>${icon("down")}</button>`;
    const archiveAction = `<button class="manage-action" data-action="toggle-managed-archive" data-kind="type" data-id="${esc(item.id)}" aria-label="${item.isArchived ? "恢复" : "停用"} ${esc(item.name)}">${icon(item.isArchived ? "show" : "hide")}<span>${item.isArchived ? "恢复" : "停用"}</span></button>`;
    const actions = `${item.isDefault ? "" : `<button class="manage-action" data-action="open-name-modal" data-kind="type" data-id="${esc(item.id)}" aria-label="修改 ${esc(item.name)}">${icon("edit")}<span>修改</span></button>`}${archiveAction}`;
    return `<div class="manage-row ${item.isArchived ? "is-hidden" : ""}"><div class="reorder-controls">${reorder}</div><div class="manage-copy"><strong>${esc(item.name)}</strong><small>${item.isDefault ? "系统默认" : "自定义类型"}${item.isArchived ? " · 已停用" : ""}</small></div><div class="manage-actions">${actions}</div></div>`;
  }

  function manageOverviewRow(panel, iconName, title, meta) {
    return `<button class="settings-row ${ui.myPanel === panel ? "is-open" : ""}" data-action="toggle-manage-panel" data-panel="${panel}" aria-expanded="${ui.myPanel === panel}"><span class="settings-icon">${icon(iconName)}</span><span class="settings-copy"><strong>${title}</strong><small>${meta}</small></span><span class="settings-next">${icon(ui.myPanel === panel ? "up" : "next")}</span></button>`;
  }

  function settingsLinkRow(page, iconName, title, meta) {
    return `<button class="settings-row" data-action="open-settings-page" data-page="${page}"><span class="settings-icon">${icon(iconName)}</span><span class="settings-copy"><strong>${title}</strong><small>${meta}</small></span><span class="settings-next">${icon("next")}</span></button>`;
  }

  function dataManagementHtml() {
    if (ui.myPanel !== "data") return "";
    const momentCount = getMoments().length;
    const localMode = !cloud.configured;
    return `<div class="data-manage-panel"><div class="data-manage-note"><span>${icon("shield")}</span><p><strong>${localMode ? "当前使用 Local Mode" : "正式内容已保存到当前账号"}</strong><small>${momentCount} 条 Moment。${localMode ? "数据保存在这个浏览器，可通过完整备份转移。" : "刷新或在其他设备登录后，会重新读取同一份云端数据。"}</small></p></div><div class="data-manage-actions"><button data-action="export-backup"><span>${icon("download")}</span><p><strong>导出备份</strong><small>Moment、类型、标签、收束、关联和图片</small></p>${icon("next")}</button><label for="backup-import" data-action="trigger-backup-import"><span>${icon("upload")}</span><p><strong>完整恢复备份</strong><small>${localMode ? "先自动备份当前本地数据，再完整替换" : "先自动备份当前账号，再由你确认完整替换"}</small></p>${icon("next")}</label><input class="sr-only" id="backup-import" type="file" accept="application/json,.json" data-backup-import data-action="file-input" data-stop-close /><button class="danger" data-action="ask-clear-local-data"><span>${icon("trash")}</span><p><strong>${localMode ? "清空本地内容" : "清空账号内容"}</strong><small>需要再次确认；默认记录类型会保留</small></p>${icon("next")}</button></div></div>`;
  }

  function renderMy() {
    const types = getTypeItems();
    const tags = getTags();
    const customTypeCount = types.filter((item) => !item.isDefault).length;
    const body = `${pageHeaderHtml("me", "MY SPACE", "我的", "管理记录方式、账号和数据备份。")}
      <section class="account-intro"><span>${icon("me")}</span><div><strong>${esc(cloud.configured ? (cloud.state.user?.email || "我的青屿空间") : "本机青屿空间")}</strong><small>${cloud.configured ? "Moment 已同步到当前账号" : "Local Mode · 数据保存在当前浏览器"}</small></div><button class="btn btn-small" data-action="open-settings-page" data-page="account">查看空间</button></section>
      <section class="settings-group"><h2>记录方式</h2><div class="settings-list" aria-label="记录方式">
          ${settingsLinkRow("types", "bookmark", "记录类型", `${DEFAULT_TYPES.length} 个默认类型${customTypeCount ? ` · ${customTypeCount} 个自定义` : ""} · 回答“为什么记”`)}
          ${settingsLinkRow("tags", "tag", "主题标签", `${tags.length} 个标签 · 回答“在讲什么”`)}
        </div></section>
      <section class="settings-group"><h2>账号数据</h2><div class="settings-list">${manageOverviewRow("data", "shield", "数据与备份", `${getMoments().length} 条 Moment · 可导出、恢复或清空`)}</div>${dataManagementHtml()}</section>`;
    app.innerHTML = shell(body, "me");
  }

  function renderTypesPage() {
    const types = getTypeItems();
    const body = `<section class="manage-page"><button class="btn btn-ghost detail-back" data-nav="me">← 返回我的</button>
      ${pageHeaderHtml("bookmark", "WHY I KEEP IT", "记录类型", "它回答“我为什么想记下这一条？”，只提供轻引导，永远不是保存门槛。")}
      <div class="manage-heading"><div><h2>当前类型顺序</h2><p>默认类型可以排序或隐藏；隐藏不会影响历史 Moment。</p></div><button class="btn btn-small btn-with-icon" data-action="open-name-modal" data-kind="type">${icon("plus")}<span>新建自定义类型</span></button></div>
      <div class="manage-list">${types.map((item, index) => typeManageRow(item, index, types.length)).join("")}</div>
      <p class="manage-rule">系统默认类型保持标准名称，不能真正删除。自定义类型停止使用后，历史 Moment 仍保留原名称。</p>
    </section>`;
    app.innerHTML = shell(body, "me");
  }

  function renderTagsPage() {
    const tags = getTagItems();
    const body = `<section class="manage-page"><button class="btn btn-ghost detail-back" data-nav="me">← 返回我的</button>
      ${pageHeaderHtml("tag", "WHAT IT IS ABOUT", "主题标签", "它回答“这一条在讲什么？”，一条 Moment 可以选择多个，也可以完全不选。")}
      <div class="manage-heading"><div><h2>当前可选标签</h2><p>人物姓名在 V1 中也可以作为普通标签使用。</p></div><button class="btn btn-small btn-with-icon" data-action="open-name-modal" data-kind="tag">${icon("plus")}<span>新建标签</span></button></div>
      <div class="tag-manage-list">${tags.length ? tags.map((tag) => `<div class="tag-manage-row ${tag.isArchived ? "is-hidden" : ""}"><span class="tag">${esc(tag.name)}${tag.isArchived ? " · 已停用" : ""}</span><div class="manage-actions"><button class="manage-action" data-action="open-name-modal" data-kind="tag" data-id="${esc(tag.id)}">${icon("edit")}<span>修改</span></button><button class="manage-action" data-action="toggle-managed-archive" data-kind="tag" data-id="${esc(tag.id)}">${icon(tag.isArchived ? "show" : "hide")}<span>${tag.isArchived ? "恢复" : "停用"}</span></button></div></div>`).join("") : '<div class="manage-empty-state"><span>${icon("tag")}</span><h2>还没有主题标签</h2><p>等你真的需要整理内容时再创建。没有标签也能正常记录。</p><button class="btn" data-action="open-name-modal" data-kind="tag">创建第一个标签</button></div>'}</div>
      <p class="manage-rule">停用只影响新记录选择器，历史 Moment 通过 tagIds 持续保留标签关联。</p>
    </section>`;
    app.innerHTML = shell(body, "me");
  }

  function renderAccountPage() {
    const localMode = !cloud.configured;
    const body = `<section class="manage-page"><button class="btn btn-ghost detail-back" data-nav="me">← 返回我的</button>
      ${pageHeaderHtml("shield", localMode ? "LOCAL MODE" : "PRIVATE ACCOUNT", localMode ? "本机空间" : "个人账号", localMode ? "当前未配置 Supabase，产品继续使用浏览器本地存储。" : "确认当前登录的账号和数据状态。")}
      <div class="account-space"><span>${icon("me")}</span><div><small>${localMode ? "当前模式" : "当前账号"}</small><strong>${localMode ? "Local Mode" : esc(cloud.state.user?.email || "")}</strong><p>${localMode ? "Moment、类型、标签与每日收束保存在 localStorage，图片保存在 IndexedDB。" : "Moment、图片、类型、标签与每日收束以云端数据为准。本机仅保留草稿和旧版迁移来源。"}</p></div></div>
      <div class="account-boundary"><h2>当前状态</h2><ul>${localMode ? "<li>今天、时间、回忆和我的均可正常使用</li><li>刷新后继续读取当前浏览器数据</li><li>配置 Supabase 后可迁移到云端账号</li>" : "<li>登录状态会在浏览器中保持</li><li>重新进入或刷新后读取账号最新数据</li><li>私有图片通过短时访问地址展示</li>"}</ul></div>
      ${localMode ? "" : '<button class="btn btn-danger" data-action="logout">退出登录</button>'}
    </section>`;
    app.innerHTML = shell(body, "me");
  }

  function detailReturnMeta() {
    const path = ui.detailReturn || "today";
    if (path === "time") return { path, label: "返回时间", active: "time" };
    if (path === "memory") return { path, label: "返回回忆", active: "memory" };
    if (path.startsWith("date/")) return { path, label: "返回这一天", active: "time" };
    if (path.startsWith("moment/")) return { path, label: "返回上一条记录", active: "memory" };
    return { path: "today", label: "返回今天", active: "today" };
  }

  function renderDetail(id, editing) {
    const moment = getMoments().find((m) => m.id === id);
    const back = detailReturnMeta();
    if (!moment) {
      app.innerHTML = shell(`<section class="detail"><button class="btn btn-ghost detail-back" data-return-path="${esc(back.path)}">← ${esc(back.label)}</button><h1 class="page-title">这条记录不存在</h1><p class="page-subtitle">它可能已经删除，或当前个人空间无法访问。</p></section>`, back.active);
      return;
    }
    if (editing) return renderEdit(moment);
    const source = moment.sourceMomentId ? getMoments().find((item) => item.id === moment.sourceMomentId) : null;
    const body = `<section class="detail">
      <button class="btn btn-ghost detail-back" data-return-path="${esc(back.path)}">← ${esc(back.label)}</button>
      <p class="eyebrow">ORIGINAL MOMENT</p>
      <h1 class="page-title">${esc(formatDateTime(moment.occurrenceAt))}</h1>
      <div class="detail-body">${esc(moment.body)}</div>
      ${photoGridHtml(moment.images || [], "readonly")}
      <div class="detail-meta">
        <span><strong>发生时间</strong>　${esc(formatDateTime(moment.occurrenceAt))}</span>
        <span><strong>创建时间</strong>　${esc(formatDateTime(moment.createdAt))}</span>
        <span><strong>记录类型</strong>　${moment.type ? esc(moment.type) : "未选择"}</span>
        <span><strong>主题标签</strong>　${moment.tags?.length ? moment.tags.map((x) => `#${esc(x)}`).join("　") : "未添加"}</span>
      </div>
      ${moment.sourceMomentId ? (source ? `<div class="source-row source-echo"><span>${icon("reply")}</span><div><small>这条记录，来自一次重新遇见 · ${esc(formatSourceDate(source.occurrenceAt))}</small><p>${esc(source.body.length > 70 ? source.body.slice(0, 70) + "…" : source.body)}</p></div><button class="btn btn-small" data-moment-id="${esc(source.id)}" data-return="moment/${esc(moment.id)}">看看当时</button></div>` : '<div class="source-row source-missing">来源记录已删除，新记录仍然保留</div>') : ""}
      ${laterMomentsHtml(moment)}
      <div class="detail-actions"><button class="btn" data-action="edit-moment" data-id="${moment.id}">编辑</button><button class="btn btn-danger" data-action="ask-delete" data-id="${moment.id}">删除</button></div>
    </section>`;
    app.innerHTML = shell(body, back.active);
  }

  function editKey(id) { return `qymj.webv1.edit.${id}`; }

  function getEditDraft(moment) {
    if (!moment) return emptyDraft();
    if (volatileEditDrafts.has(moment.id)) return withEditorAliases(canonicalEditorData(volatileEditDrafts.get(moment.id)));
    const saved = read(editKey(moment.id), null);
    return withEditorAliases(saved ? canonicalEditorData(saved) : canonicalEditorData(moment));
  }

  function saveEditDraft(id, patch) {
    const moment = getMoments().find((m) => m.id === id);
    const next = withEditorAliases(canonicalEditorData({ ...getEditDraft(moment), ...patch }));
    if (tryWrite(editKey(id), next)) volatileEditDrafts.delete(id);
    else volatileEditDrafts.set(id, next);
    return next;
  }

  function renderEdit(moment) {
    const data = getEditDraft(moment);
    const back = detailReturnMeta();
    const body = `<section class="detail">
      <button class="btn btn-ghost detail-back" data-action="exit-edit" data-id="${moment.id}">← 退出编辑（保留草稿）</button>
      <p class="eyebrow">EDIT MOMENT</p><h1 class="page-title">编辑原记录</h1>
      <div class="edit-form">
        <div class="field"><label class="field-label" for="edit-body">正文</label>${momentEditorBodyHtml(data, "edit", moment.id)}</div>
        ${expandedFieldsHtml(data, "edit", moment.id)}
        <div class="form-actions"><button class="btn" data-action="discard-edit" data-id="${moment.id}">放弃修改</button><button class="btn btn-primary" data-action="save-edit" data-id="${moment.id}" ${data.body.trim() ? "" : "disabled"}>保存修改</button></div>
      </div>
    </section>`;
    app.innerHTML = shell(body, back.active);
  }

  function renderClosure() {
    const date = todayKey();
    const moments = sortMoments(getMoments().filter((m) => String(m.occurrenceAt).slice(0, 10) === date));
    if (!moments.length) return go("today");
    const saved = getClosures()[date] || { answers: ["", "", ""], finalLine: "" };
    const draftMap = read(STORAGE.closureDraft, {});
    const data = draftMap[date] || saved;
    const any = (data.answers || []).some((x) => x.trim()) || (data.finalLine || "").trim();
    const body = `<section class="detail">
      <button class="btn btn-ghost detail-back" data-nav="today">← 返回今天</button>
      <p class="eyebrow">DAILY CLOSURE · OPTIONAL</p><h1 class="page-title">给今天收个尾</h1>
      <p class="page-subtitle">这里没有任务，也没有完成率。挑想答的，或者只留一句话。</p>
      <div class="today-summary"><strong>今天留下的 ${moments.length} 条 Moment</strong>${moments.map((m) => `<p>${formatTime(m.occurrenceAt)}　${esc(m.body.slice(0, 48))}${m.body.length > 48 ? "…" : ""}</p>`).join("")}</div>
      <div class="closure-form">
        ${DAILY_QUESTIONS.map((question, i) => `<div class="question"><label for="closure-q-${i}">${esc(question)}</label><textarea id="closure-q-${i}" data-closure-answer="${i}" placeholder="可以跳过">${esc(data.answers?.[i] || "")}</textarea></div>`).join("")}
        <div class="question"><label for="closure-final">给今天留一句话</label><textarea id="closure-final" data-closure-final placeholder="只写这一项也可以">${esc(data.finalLine || "")}</textarea></div>
        <div class="form-actions"><button class="btn btn-primary" id="closure-save" data-action="save-closure" ${any ? "" : "disabled"}>${getClosures()[date] ? "更新今日收束" : "保存今日收束"}</button></div>
      </div>
    </section>`;
    app.innerHTML = shell(body, "today");
  }

  const UI_GALLERY_GROUPS = [
    ["入口", [
      ["ENTRY-01", "首次进入 · 三句话理解产品", "页面", "login"], ["ENTRY-02", "首条记录纯净态", "状态", "empty-today"], ["ENTRY-03", "内容积累后的演示空间", "状态", "demo-today"],
    ]],
    ["今天", [
      ["TODAY-01", "今天 · 多条 Moment", "页面", "demo-today"], ["TODAY-02", "今天 · 无 Moment", "空状态", "empty-today"], ["TODAY-03", "今天 · 一条 Moment", "状态", "one-today"],
    ]],
    ["记录", [
      ["RECORD-01", "只写一句的默认态", "内嵌组件", "empty-today"], ["RECORD-02", "输入后出现可选补充", "渐进状态", "draft"], ["RECORD-03", "想多写一点", "展开区", "full-record"],
      ["RECORD-04", "记录类型轻引导", "选择器", "type-guide"], ["RECORD-05", "图片达到 3 张", "边界状态", "image-limit"], ["RECORD-06", "保存失败且保留草稿", "异常", "failed-save"],
    ]],
    ["Moment", [
      ["MOMENT-01", "普通详情", "页面", "moment"], ["MOMENT-02", "带图片、类型与标签", "状态", "moment-rich"], ["MOMENT-03", "编辑原记录", "编辑态", "moment-edit"],
      ["MOMENT-04", "带来源关联", "状态", "moment-linked"], ["MOMENT-05", "删除确认与撤销", "弹窗 / 提示", "moment-delete"], ["MOMENT-06", "详情不存在", "异常", "moment-missing"],
    ]],
    ["每日收束", [
      ["CLOSURE-01", "今天收束邀请", "状态", "demo-today"], ["CLOSURE-02", "首次创建", "页面", "closure-empty"], ["CLOSURE-03", "已有收束与修改", "编辑态", "closure"],
    ]],
    ["时间", [
      ["TIME-01", "时间线 · 长期沉淀", "页面", "timeline"], ["TIME-02", "时间线 · 无历史", "空状态", "empty-time"], ["TIME-03", "日历视图", "页面状态", "calendar"],
      ["DATE-01", "某一天内容", "页面", "date"], ["DATE-02", "无内容日期", "空状态", "empty-date"],
    ]],
    ["回忆", [
      ["MEMORY-01", "带时间感重新遇见", "页面", "memory"], ["MEMORY-02", "看看那一天 / 再捡一条", "交互状态", "memory"], ["MEMORY-03", "写下现在想到的", "关联创建页", "reply"],
      ["MEMORY-04", "没有回忆候选", "空状态", "empty-memory"], ["MEMORY-05", "候选较少", "边界状态", "single-memory"],
    ]],
    ["我的", [
      ["PROFILE-01", "我的", "页面", "me"], ["TYPE-01", "记录类型管理", "页面", "types"], ["TYPE-02", "新建自定义类型", "弹窗", "new-type"],
      ["TAG-01", "主题标签管理", "页面", "tags"], ["TAG-02", "无标签", "空状态", "empty-tags"], ["ACCOUNT-01", "个人私有空间", "页面", "account"],
    ]],
    ["关键状态与通用组件", [
      ["STATE-01", "页面加载骨架", "加载状态", "review-loading"], ["STATE-02", "内容读取失败", "失败状态", "review-error"], ["STATE-03", "保存成功反馈", "Toast", "review-success"],
      ["STATE-04", "删除后 10 秒撤销", "Toast / 状态", "review-undo"], ["STATE-05", "图片添加失败", "上传失败", "review-image-error"], ["STATE-06", "编辑未保存退出", "Dialog", "review-dirty"],
      ["STATE-07", "来源记录已删除", "边界状态", "review-source-missing"], ["STATE-08", "低频补充 Sheet", "Sheet", "review-sheet"], ["STATE-09", "退出确认", "Dialog", "review-logout"],
    ]],
  ];

  function galleryCardHtml(item) {
    const [id, name, type, preset] = item;
    return `<article class="gallery-card"><div><span class="gallery-id">${id}</span><span class="gallery-type">${type}</span></div><h3>${name}</h3><button class="btn btn-small" data-action="gallery-open" data-preset="${preset}">查看界面</button></article>`;
  }

  function renderGallery() {
    const coreFlows = [
      ["第一次记录", "第一次进入 → 写一句话 → 保存", "flow-first", "pen"],
      ["今天记录闭环", "今天 → 记录 → 保存 → 内容流", "flow-record", "today"],
      ["沿时间回看", "时间 → 时间线 / 日历 → 过去的记录", "flow-time", "time"],
      ["让过去再次发生", "回忆 → 旧记录 → 写下现在 → 来源关联", "flow-memory", "memory"],
      ["整理记录方式", "我的 → 记录类型 / 主题标签", "flow-organize", "me"],
    ];
    const narrative = [
      ["降低记录成本", "今天首屏只需要输入一句正文并点击“记下来”。", "demo-today"],
      ["兼顾轻量与深度表达", "同一草稿可以自然展开长文、图片、类型、标签与发生时间。", "full-record"],
      ["记录类型提供轻引导", "九种类型回答“为什么记”，可以跳过。", "type-guide"],
      ["Moment 长期沉淀", "时间线按发生时间组织，不使用数据 Dashboard。", "timeline"],
      ["今日轻量回顾", "从当天 Moment 出发完成可选收束，没有打卡与完成率。", "closure"],
      ["旧内容重新产生价值", "随机回忆可以换一条、打开原文并触发新的记录。", "memory"],
      ["保留不同时点的轨迹", "新 Moment 保存来源关系，旧 Moment 原文保持不变。", "moment-linked"],
      ["解释青屿漫记是什么", "入口、首页记录器、时间与回忆共同说明产品用途。", "login"],
      ["完整产品闭环", "记录 → 时间 / 收束 → 回忆 → 关联新 Moment。", "demo-today"],
    ];
    const body = `<section class="gallery-page">
      <header class="gallery-header"><div><p class="eyebrow">V1 WEB · UI REVIEW</p><h1>青屿漫记 UI 总览</h1><p>集中审核全部正式页面、关键状态和作品集叙事证据。这里是内部 Review 页面，不属于正式产品导航。</p></div><div class="gallery-actions"><button class="btn btn-primary" data-action="gallery-open" data-preset="demo-today">进入完整演示</button><button class="btn" data-action="gallery-open" data-preset="empty-today">查看新用户空状态</button></div></header>
      <section class="core-flows" aria-labelledby="core-flows-title"><div class="gallery-section-title"><span id="core-flows-title">Core Flows</span><small>从真实起点直接体验 5 条核心路径</small></div><div class="core-flow-grid">${coreFlows.map(([title, path, preset, iconName], index) => `<button data-action="gallery-open" data-preset="${preset}"><span class="core-flow-icon">${icon(iconName)}</span><i>0${index + 1}</i><strong>${title}</strong><small>${path}</small><b>开始体验 ${icon("next")}</b></button>`).join("")}</div></section>
      <section class="narrative-check"><div class="gallery-section-title"><span>作品集叙事检查</span><small>9 / 9 均有真实页面或交互证据</small></div><div class="narrative-grid">${narrative.map(([title, proof, preset], index) => `<button data-action="gallery-open" data-preset="${preset}"><i>${String(index + 1).padStart(2, "0")}</i><span><strong>${title}</strong><small>${proof}</small></span>${icon("next")}</button>`).join("")}</div></section>
      ${UI_GALLERY_GROUPS.map(([group, cases]) => `<section class="gallery-group"><div class="gallery-section-title"><span>${group}</span><small>${cases.length} 个 Review Case</small></div><div class="gallery-grid">${cases.map(galleryCardHtml).join("")}</div></section>`).join("")}
    </section>`;
    app.innerHTML = shell(body, "gallery");
  }

  function renderReviewCase(kind) {
    const titles = {
      loading: ["加载骨架", "保持页面结构，避免把等待误认成空白。"],
      error: ["内容暂时没有回来", "记录入口仍可使用，读取失败的内容可以单独重试。"],
      success: ["保存成功", "反馈轻量出现，不打断下一次记录。"],
      undo: ["删除后撤销", "删除成功后提供 10 秒恢复窗口，不建立回收站。"],
      "image-error": ["图片没有添加成功", "正文与其他已选信息保持不变，可以重试或继续保存。"],
      dirty: ["未保存修改", "离开编辑前聚焦一次选择，避免误丢当前修改。"],
      "source-missing": ["来源记录已删除", "新 Moment 保持完整，只取消失效的来源跳转。"],
      sheet: ["低频补充信息", "类型、标签与时间在需要时展开，不挤占首屏记录动作。"],
      logout: ["退出确认", "退出后停止展示个人内容。"],
    };
    const [title, subtitle] = titles[kind] || ["界面状态", "用于统一审核 V1 的反馈语言。"];
    let specimen = "";
    if (kind === "loading") specimen = `<div class="state-skeleton"><i></i><b></b><b></b><b></b><span></span><span></span></div>`;
    else if (kind === "error") specimen = `<div class="state-message state-error">${icon("leaf")}<div><strong>今天留下的内容暂时没有回来</strong><p>你仍然可以在上方记下新的 Moment。稍后再试一次就好。</p></div><button class="btn">重新读取</button></div>`;
    else if (kind === "success") specimen = `<div class="state-stage"><div class="toast state-static-toast"><span>已保存，安静地放进今天了</span></div></div>`;
    else if (kind === "undo") specimen = `<div class="state-stage"><div class="toast state-static-toast"><span>已删除</span><button>撤销（10 秒）</button></div></div>`;
    else if (kind === "image-error") specimen = `<div class="upload-error"><span>${icon("memory")}</span><div><strong>“傍晚天空.jpg”没有添加成功</strong><p>网络似乎停了一下，正文和其他图片都还在。</p></div><button class="btn btn-small">重试</button><button class="btn btn-ghost btn-small">移除</button></div>`;
    else if (kind === "source-missing") specimen = `<article class="memory-card"><div class="memory-card-top"><span>现在的想法</span><span>关联已失效</span></div><div class="memory-quote">重新看到过去的自己之后，我才意识到，有些变化一直安静地发生着。</div><div class="source-row source-missing">${icon("reply")}<span>来源记录已删除</span></div></article>`;
    else if (kind === "sheet") specimen = `<section class="review-sheet"><span class="sheet-handle"></span><h3>补充一点记录信息</h3>${expandedFieldsHtml({ images: ["tree"], tags: ["散步", "天气"], occurrenceAt: nowLocalValue() }, "draft")}</section>`;
    else specimen = `<div class="state-message">${icon(kind === "logout" ? "logout" : "edit")}<div><strong>${esc(title)}</strong><p>${esc(subtitle)}</p></div><div class="state-actions"><button class="btn">取消</button><button class="btn ${kind === "logout" ? "btn-danger" : "btn-primary"}">${kind === "logout" ? "确认退出" : "保留修改"}</button></div></div>`;
    const body = `<section class="review-case"><button class="btn btn-ghost detail-back" data-nav="ui-gallery">← 返回 UI 总览</button>${pageHeaderHtml("memory", "STATE REVIEW", title, subtitle)}${specimen}</section>`;
    app.innerHTML = shell(body, "");
  }

  function applyGalleryPreset(preset) {
    const openDemo = () => { seedDemoSpace(); };
    if (preset === "flow-first") { write(STORAGE.auth, false); return go("login"); }
    if (preset === "flow-record") { openDemo(); remove(STORAGE.draft); return go("today"); }
    if (preset === "flow-time") { openDemo(); ui.timeView = "timeline"; return go("time"); }
    if (preset === "flow-memory") { openDemo(); ui.memoryCurrentId = "demo-source-old"; return go("memory"); }
    if (preset === "flow-organize") { openDemo(); return go("me"); }
    if (preset.startsWith("review-")) { openDemo(); return go(`review/${preset.slice(7)}`); }
    if (preset === "login") { write(STORAGE.auth, false); return go("login"); }
    if (preset === "empty-today") { startEmptySpace(); return go("today"); }
    if (preset === "demo-today") { openDemo(); return go("today"); }
    if (preset === "one-today") {
      openDemo();
      setMoments(getMoments().filter((moment) => moment.id === "demo-today-coffee"));
      return go("today");
    }
    if (preset === "draft") {
      openDemo(); saveDraft({ body: "刚刚在路边闻到一阵桂花香。", expanded: false }); return go("today");
    }
    if (preset === "full-record") {
      openDemo(); saveDraft({ body: "今天想把这件事写得更完整一点。", images: ["desk"], type: "此刻感受", tags: ["成长"], expanded: true }); return go("today");
    }
    if (preset === "type-guide") {
      openDemo(); saveDraft({ body: "", type: "", expanded: false }); ui.typeGuideOpen = true; go("today"); return setTimeout(renderToday, 0);
    }
    if (preset === "image-limit") {
      openDemo(); saveDraft({ body: "今天想把看到的几处光影一起留下。", images: ["tree", "window", "desk"], expanded: true }); return go("today");
    }
    if (preset === "failed-save") {
      openDemo(); saveDraft({ body: "这条内容会模拟一次保存失败，但草稿不会丢失。", expanded: false }); write(STORAGE.failNextSave, true); return go("today");
    }
    if (["moment", "moment-rich", "moment-edit", "moment-linked", "moment-delete"].includes(preset)) {
      openDemo();
      const id = preset === "moment-rich" ? "demo-today-sunset" : preset === "moment-linked" ? "demo-linked-now" : "demo-today-thought";
      go(`moment/${id}${preset === "moment-edit" ? "/edit" : ""}`);
      if (preset === "moment-delete") setTimeout(() => showDeleteModal(id), 60);
      return;
    }
    if (preset === "moment-missing") { openDemo(); return go("moment/not-found"); }
    if (preset === "closure-empty") {
      openDemo(); const closures = read(STORAGE.closures, {}); delete closures[todayKey()]; write(STORAGE.closures, closures); return go("closure");
    }
    if (preset === "closure") { openDemo(); return go("closure"); }
    if (preset === "timeline") { openDemo(); ui.timeView = "timeline"; return go("time"); }
    if (preset === "empty-time") { startEmptySpace(); ui.timeView = "timeline"; return go("time"); }
    if (preset === "calendar") { openDemo(); ui.timeView = "calendar"; ui.calendarMonth = todayKey().slice(0, 7); return go("time"); }
    if (preset === "date") { openDemo(); return go(`date/${localValueDaysAgo(1, 12, 0).slice(0, 10)}`); }
    if (preset === "empty-date") { openDemo(); return go(`date/${localValueDaysAgo(3, 12, 0).slice(0, 10)}`); }
    if (preset === "memory") { openDemo(); ui.memoryCurrentId = "demo-source-old"; return go("memory"); }
    if (preset === "reply") { openDemo(); return go("continue/demo-source-old"); }
    if (preset === "empty-memory") { startEmptySpace(); return go("memory"); }
    if (preset === "single-memory") {
      openDemo(); setMoments(getMoments().filter((moment) => moment.id === "demo-source-old")); ui.memoryCurrentId = ""; return go("memory");
    }
    if (preset === "me") { openDemo(); return go("me"); }
    if (preset === "types") { openDemo(); return go("types"); }
    if (preset === "new-type") { openDemo(); go("types"); return setTimeout(() => showNameModal("type", "", ""), 60); }
    if (preset === "tags") { openDemo(); return go("tags"); }
    if (preset === "empty-tags") { openDemo(); write(STORAGE.tags, []); return go("tags"); }
    if (preset === "account") { openDemo(); return go("account"); }
  }

  function render() {
    overlayRoot.innerHTML = "";
    if (!cloud.state.ready) {
      app.innerHTML = `<main class="app-loading"><span class="brand-mark">${icon("leaf")}</span><strong>正在打开青屿漫记…</strong></main>`;
      return;
    }
    const r = route();
    if (!isAuthed()) {
      if (r.name !== "login") return go("login");
      return renderLogin();
    }
    if (r.name === "login") return go("today");
    if (r.name === "today") return renderToday();
    if (r.name === "record") return renderRecord();
    if (r.name === "time") return renderTime();
    if (r.name === "memory") return renderMemory();
    if (r.name === "me") return renderMy();
    if (r.name === "types") return renderTypesPage();
    if (r.name === "tags") return renderTagsPage();
    if (r.name === "account") return renderAccountPage();
    if (r.name === "date") return renderDate(r.id || todayKey());
    if (r.name === "ui-gallery") return renderGallery();
    if (r.name === "review") return renderReviewCase(r.id);
    if (r.name === "reply" || r.name === "continue") return renderReply(r.id);
    if (r.name === "moment") return renderDetail(r.id, r.mode === "edit");
    if (r.name === "closure") return renderClosure();
    go("today");
  }

  function updateSaveButton() {
    const button = document.getElementById("quick-save");
    const textarea = document.getElementById("draft-body");
    if (button && textarea) button.disabled = !textarea.value.trim();
    const count = document.querySelector(".composer .char-count");
    if (count && textarea) count.textContent = `${textarea.value.length} 字`;
  }

  function showToast(message, actionLabel, action) {
    clearTimeout(ui.toastTimer);
    toastRoot.innerHTML = `<div class="toast" role="status"><span>${esc(message)}</span>${actionLabel ? `<button data-toast-action="${esc(action)}">${esc(actionLabel)}</button>` : ""}</div>`;
    if (action !== "undo-delete") ui.toastTimer = setTimeout(() => { toastRoot.innerHTML = ""; }, 5200);
  }

  function showGlobalRecorder() {
    const draft = getDraft();
    const progressiveVisible = hasDraftContent(draft);
    const typeInfo = getTypeItems().find((item) => item.id === draft.recordTypeId);
    overlayRoot.innerHTML = `<div class="modal-backdrop global-record-backdrop" data-action="close-modal">
      <section class="global-recorder" role="dialog" aria-modal="true" aria-labelledby="global-record-title" data-stop-close>
        <header class="global-recorder-header">
          <div><p class="eyebrow">QUICK MOMENT</p><h2 id="global-record-title">这一刻，想留下什么？</h2></div>
          <button class="global-recorder-close" data-action="close-modal" aria-label="关闭快速记录">×</button>
        </header>
        <div class="draft-status ${ui.draftSaveState === "saving" ? "is-saving" : ""}" data-global-draft-indicator ${progressiveVisible ? "" : "hidden"}><span class="draft-dot"></span>${draftSaveLabel()}</div>
        <label class="sr-only" for="global-draft-body">Moment 正文</label>
        ${momentEditorBodyHtml(draft, "global", "", "写一句也可以……")}
        ${ui.inlineError ? `<div class="inline-error" role="alert">${esc(ui.inlineError)}</div>` : ""}
        ${draft.expanded ? expandedFieldsHtml(draft, "global") : ""}
        ${ui.globalTypeGuideOpen ? typeGuideHtml(draft.type, "global") : ""}
        <div class="global-recorder-actions" data-global-progressive ${progressiveVisible ? "" : "hidden"}>
          <div class="global-secondary-actions">
            <button class="btn btn-ghost btn-small btn-with-icon" data-action="toggle-global-types">${icon("bookmark")}<span>${draft.type || "你想记录什么？"}</span></button>
            <button class="btn btn-ghost btn-small btn-with-icon" data-action="toggle-global-expand">${icon("expand")}<span>${draft.expanded ? "收起完整记录" : "写得更完整一些"}</span></button>
          </div>
          <button class="btn btn-primary btn-save" id="global-save" data-action="save-global-moment" ${draft.body.trim() ? "" : "disabled"}>${icon("check")}<span>保存</span></button>
        </div>
        ${typeInfo?.description && !ui.globalTypeGuideOpen ? `<div class="type-note" data-global-type-note ${progressiveVisible ? "" : "hidden"}>${esc(typeInfo.description)}</div>` : ""}
      </section>
    </div>`;
    setTimeout(() => document.getElementById("global-draft-body")?.focus(), 0);
  }

  function updateGlobalRecorder() {
    const textarea = document.getElementById("global-draft-body");
    if (!textarea) return;
    const visible = Boolean(textarea.value.trim());
    const button = document.getElementById("global-save");
    const actions = document.querySelector("[data-global-progressive]");
    const indicator = document.querySelector("[data-global-draft-indicator]");
    if (button) button.disabled = !visible;
    if (actions) actions.hidden = !visible;
    if (indicator) indicator.hidden = !visible;
  }

  function imageTargetData(target, momentId = "") {
    if (target === "draft" || target === "global") return getDraft();
    if (target === "reply") return getReplyDraft(momentId || route().id || ui.memoryCurrentId);
    const id = momentId || route().id;
    return getEditDraft(getMoments().find((moment) => moment.id === id));
  }

  function updateImageTarget(target, momentId, images) {
    if (target === "draft" || target === "global") return saveDraft({ images, expanded: true });
    if (target === "reply") return saveReplyDraft(momentId || route().id || ui.memoryCurrentId, { images, expanded: true });
    return saveEditDraft(momentId || route().id, { images });
  }

  async function saveImageFiles(files, target, momentId = "") {
    const data = imageTargetData(target, momentId);
    const room = Math.max(0, 3 - data.images.length);
    const chosen = [...files].filter((file) => file.type.startsWith("image/")).slice(0, room);
    if (!chosen.length) {
      ui.imageUploadError = room ? "请选择 JPG、PNG、WebP 或其他图片文件。" : "一条记录最多添加 3 张图片。";
      return showImagePicker(target, momentId);
    }
    ui.imageUploadError = "";
    ui.imageUploading = true;
    showImagePicker(target, momentId);
    const added = [];
    try {
      for (const file of chosen) {
        const compressed = await compressImage(file);
        const storedFile = new File([compressed.blob], compressed.name, { type: compressed.mimeType });
        added.push(await putImageAsset(storedFile));
        updateImageTarget(target, momentId, [...data.images, ...added]);
      }
    } catch (error) {
      ui.imageUploadError = error?.message || "图片没有保存成功，正文和已选内容仍然保留。";
    }
    ui.imageUploading = false;
    showImagePicker(target, momentId);
  }

  function showImagePicker(target, momentId = "") {
    ui.imageTarget = target;
    ui.imageMomentId = momentId;
    const data = imageTargetData(target, momentId);
    const doneAction = target === "global" ? "return-global-record" : "close-modal";
    overlayRoot.innerHTML = `<div class="modal-backdrop" data-action="close-modal"><section class="modal local-image-modal" role="dialog" aria-modal="true" aria-labelledby="image-title" data-stop-close>
      <h2 id="image-title">添加图片</h2><p>${cloud.configured ? "选择后会先在本机压缩，保存 Moment 时上传到你的私有云端空间。" : "选择后会先在本机压缩，并保存到当前浏览器。"}单张原始文件最大 20 MB。</p>
      ${photoGridHtml(data.images, target, momentId)}
      ${ui.imageUploadError ? `<div class="inline-error" role="alert">${esc(ui.imageUploadError)}</div>` : ""}
      <label class="local-file-button ${data.images.length >= 3 || ui.imageUploading ? "is-disabled" : ""}" for="image-file-input" data-action="trigger-image-upload">${icon("image")}<span>${ui.imageUploading ? "正在保存图片…" : data.images.length >= 3 ? "已添加 3 / 3" : `选择本地图片 · 还可添加 ${3 - data.images.length} 张`}</span></label>
      <input class="sr-only" id="image-file-input" type="file" accept="image/*" multiple data-image-upload="${esc(target)}" data-moment-id="${esc(momentId)}" data-action="file-input" data-stop-close ${data.images.length >= 3 || ui.imageUploading ? "disabled" : ""} />
      <div class="modal-actions"><button class="btn" data-action="${doneAction}">完成</button></div>
    </section></div>`;
    queueImageHydration();
  }

  function showImageViewer(id) {
    const label = imageLabel(id);
    const visual = String(id).startsWith("img:") || String(id).startsWith("cloud:") ? `<img class="image-viewer-photo local-photo" data-local-image="${esc(id)}" alt="${esc(label)}" />` : `<div class="image-viewer-photo photo-${esc(id)}"><span>${esc(label)}</span></div>`;
    overlayRoot.innerHTML = `<div class="modal-backdrop image-viewer-backdrop" data-action="close-modal"><section class="image-viewer" role="dialog" aria-modal="true" aria-label="查看图片" data-stop-close><button class="image-viewer-close" data-action="close-modal" aria-label="关闭大图">×</button>${visual}<small>${esc(label)}</small></section></div>`;
    queueImageHydration();
  }

  async function buildBackupPayload() {
    const moments = getMoments();
    const refMap = new Map();
    const images = [];
    for (const moment of moments) {
      for (const ref of moment.images || []) {
        if (refMap.has(ref)) continue;
        const backupId = `img:backup:${makeId()}`;
        refMap.set(ref, backupId);
        if (String(ref).startsWith("cloud:")) {
          const meta = cloud.imageMeta(ref);
          const response = await fetch(await cloud.signedImageUrl(ref));
          if (!response.ok) throw new Error(`图片“${meta?.name || "生活图片"}”下载失败`);
          const blob = await response.blob();
          images.push({ id: backupId, name: meta?.name || "生活图片", type: meta?.mimeType || blob.type, size: blob.size, createdAt: new Date().toISOString(), dataUrl: await blobToDataUrl(blob) });
        } else if (String(ref).startsWith("img:")) {
          const asset = await getImageAsset(ref);
          if (!asset?.blob) throw new Error(`图片“${imageLabel(ref)}”无法读取`);
          images.push({ id: backupId, name: asset.name, type: asset.type, size: asset.size, createdAt: asset.createdAt, dataUrl: await blobToDataUrl(asset.blob) });
        }
      }
    }
    return {
      format: "qingyu-moment-backup",
      version: 3,
      product: "青屿漫记",
      exportedAt: new Date().toISOString(),
      data: {
        dataVersion: DATA_VERSION,
        moments: moments.map((moment) => ({ ...moment, images: (moment.images || []).map((ref) => refMap.get(ref)).filter(Boolean) })),
        types: getTypeItems(),
        typeOrder: getTypeItems().map((item) => item.id),
        tags: getTagItems(),
        dailyClosures: getClosures(),
      },
      images,
    };
  }

  function downloadBackup(backup, prefix = "青屿漫记备份") {
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url; anchor.download = `${prefix}_${todayKey()}.json`;
    document.body.appendChild(anchor); anchor.click(); anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function exportBackup(beforeImport = false) {
    showToast("正在整理备份…");
    try {
      const backup = await buildBackupPayload();
      downloadBackup(backup, beforeImport ? "青屿漫记_导入前自动备份" : "青屿漫记备份");
      showToast(`${beforeImport ? "导入前备份" : "备份"}已导出 · ${backup.data.moments.length} 条 Moment`);
      return backup;
    } catch (error) {
      if (error?.name === "AbortError") { showToast("已取消导出"); return; }
      showToast(error?.message || "备份导出失败，账号内容没有发生变化");
      throw error;
    }
  }

  function validateBackup(raw) {
    if (!raw || raw.format !== "qingyu-moment-backup" || ![1, 2, 3].includes(Number(raw.version))) throw new Error("这不是青屿漫记支持的备份文件");
    const data = raw.data;
    if (!data || !Array.isArray(data.moments) || !Array.isArray(data.tags) || !data.dailyClosures || typeof data.dailyClosures !== "object") throw new Error("备份文件缺少必要的数据");
    const importedTypes = Array.isArray(data.types) ? data.types : [];
    const legacyCustoms = Array.isArray(data.customTypes) ? data.customTypes : [];
    const hidden = new Set(Array.isArray(data.hiddenTypes) ? data.hiddenTypes : []);
    const types = importedTypes.length ? importedTypes.map((item) => ({
      id: String(item.id || `type:import:${makeId()}`),
      name: String(item.name || "").trim(),
      icon: item.icon || "leaf",
      description: item.description || "",
      sortOrder: Number(item.sortOrder ?? item.sort_order ?? 999),
      isArchived: Boolean(item.isArchived ?? item.hidden),
      isDefault: Boolean(item.isDefault ?? item.isSystem),
      isSystem: Boolean(item.isSystem ?? item.isDefault),
      createdAt: item.createdAt || new Date().toISOString(),
    })).filter((item) => item.name) : DEFAULT_TYPES.map((preset, index) => ({ id: preset.id, name: preset.name, icon: "leaf", description: preset.description, sortOrder: (index + 1) * 10, isArchived: hidden.has(preset.name), isDefault: true, isSystem: true, createdAt: "2026-01-01T00:00:00.000Z" }));
    DEFAULT_TYPES.forEach((preset, index) => {
      if (!types.some((item) => item.name === preset.name)) types.push({ id: preset.id, name: preset.name, icon: "leaf", description: preset.description, sortOrder: (index + 1) * 10, isArchived: hidden.has(preset.name), isDefault: true, isSystem: true, createdAt: "2026-01-01T00:00:00.000Z" });
    });
    [...importedTypes, ...legacyCustoms].forEach((item) => {
      const name = String(item?.name || "").trim();
      if (!name || types.some((existing) => existing.name === name)) return;
      types.push({ id: String(item.id || `type:${makeId()}`), name, description: String(item.description || "自定义记录类型"), isArchived: Boolean(item.isArchived ?? item.hidden), isDefault: false, createdAt: item.createdAt || new Date().toISOString() });
    });
    const tags = [];
    data.tags.forEach((item) => {
      const tag = typeof item === "string" ? { id: `tag:import:${makeId()}`, name: item, isArchived: false, createdAt: new Date().toISOString() } : { id: String(item?.id || `tag:import:${makeId()}`), name: String(item?.name || "").trim(), isArchived: Boolean(item?.isArchived), createdAt: item?.createdAt || new Date().toISOString() };
      if (tag.name && !tags.some((existing) => existing.id === tag.id || existing.name === tag.name)) tags.push(tag);
    });
    data.moments.flatMap((moment) => Array.isArray(moment?.tags) ? moment.tags : []).forEach((rawName) => {
      const name = String(rawName || "").trim();
      if (name && !tags.some((item) => item.name === name)) tags.push({ id: `tag:import:${makeId()}`, name, isArchived: false, createdAt: new Date().toISOString() });
    });
    const ids = new Set();
    const moments = data.moments.map((moment) => {
      const content = String(moment?.content ?? moment?.body ?? "").trim();
      if (!moment || !content || !moment.id || ids.has(String(moment.id))) throw new Error("备份中存在无效或重复的 Moment");
      ids.add(String(moment.id));
      const recordTypeId = String(moment.recordTypeId || types.find((item) => item.name === moment.type)?.id || "");
      const tagIds = Array.isArray(moment.tagIds) ? moment.tagIds.map(String) : (moment.tags || []).map((name) => tags.find((item) => item.name === name)?.id).filter(Boolean);
      const normalized = { id: String(moment.id), content, images: Array.isArray(moment.images) ? [...new Set(moment.images.map(String))].slice(0, 3) : [], recordTypeId: types.some((item) => item.id === recordTypeId) ? recordTypeId : null, tagIds: [...new Set(tagIds)].filter((id) => tags.some((item) => item.id === id)), occurredAt: moment.occurredAt || moment.occurrenceAt, createdAt: moment.createdAt, sourceMomentId: moment.sourceMomentId || moment.sourceId || null };
      if (Number.isNaN(new Date(normalized.occurredAt).getTime()) || Number.isNaN(new Date(normalized.createdAt).getTime())) throw new Error("备份中存在无法识别的时间");
      return normalized;
    });
    const images = Array.isArray(raw.images) ? raw.images : [];
    images.forEach((image) => {
      if (!image?.id || !String(image.id).startsWith("img:") || !String(image.dataUrl || "").startsWith("data:image/")) throw new Error("备份中存在无法识别的图片");
    });
    const imageIds = new Set(images.map((image) => image.id));
    if (moments.some((moment) => moment.images.some((id) => String(id).startsWith("img:") && !imageIds.has(id)))) throw new Error("备份缺少 Moment 引用的图片文件");
    return {
      moments,
      types,
      typeOrder: Array.isArray(data.typeOrder) ? data.typeOrder : [],
      tags,
      closures: data.dailyClosures,
      images,
      exportedAt: raw.exportedAt || "",
    };
  }

  async function readBackupFile(file) {
    try {
      if (!file || file.size > 120 * 1024 * 1024) throw new Error("备份文件过大，无法在浏览器中安全读取");
      const backup = validateBackup(JSON.parse(await file.text()));
      ui.preImportBackup = await exportBackup(true);
      ui.pendingImport = backup;
      overlayRoot.innerHTML = `<div class="modal-backdrop" data-action="close-modal"><section class="modal import-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="import-title" data-stop-close><span class="modal-symbol">${icon("upload")}</span><h2 id="import-title">用这份备份完整恢复账号？</h2><p>文件中有 ${backup.moments.length} 条 Moment、${backup.tags.length} 个标签和 ${backup.images.length} 张图片。</p><div class="import-impact"><strong>当前账号数据已自动下载备份</strong><small>确认后，账号现有数据会被这份文件完整替换；不会执行复杂合并。</small></div><div class="modal-actions"><button class="btn" data-action="close-modal">取消</button><button class="btn btn-primary" data-action="confirm-import-backup">确认恢复</button></div></section></div>`;
    } catch (error) {
      ui.pendingImport = null;
      showToast(error?.message || "备份文件无法读取");
    }
  }

  async function applyImportBackup() {
    const backup = ui.pendingImport;
    if (!backup) return;
    if (!cloud.configured) {
      try {
        await clearImageAssets();
        clearPrototypeUserData(false);
        write(STORAGE.moments, backup.moments);
        write(STORAGE.typeOrder, backup.typeOrder);
        write(STORAGE.types, backup.types);
        write(STORAGE.tags, backup.tags);
        write(STORAGE.closures, backup.closures);
        const meta = {};
        for (const image of backup.images) {
          const blob = dataUrlToBlob(image.dataUrl);
          const record = { id: image.id, blob, name: image.name || "生活图片", type: image.type || blob.type, size: blob.size, createdAt: image.createdAt || new Date().toISOString() };
          await putImportedImageAsset(record);
          meta[image.id] = { name: record.name, type: record.type, size: record.size, createdAt: record.createdAt };
        }
        write(STORAGE.imageMeta, meta);
        migrateStoredData();
        ui.pendingImport = null; ui.preImportBackup = null; overlayRoot.innerHTML = ""; go("today");
        setTimeout(() => showToast(`本地数据已恢复 · ${backup.moments.length} 条 Moment`), 0);
      } catch (error) { showToast(error?.message || "本地备份恢复失败，自动备份文件可用于重试"); }
      return;
    }
    const asSnapshot = (value) => ({ moments: value.moments, types: value.types, tags: value.tags, closures: value.closures, imageIds: value.images.map((image) => image.id) });
    const loader = (value) => async (id) => {
      const image = value.images.find((item) => item.id === id);
      if (!image) return null;
      const blob = dataUrlToBlob(image.dataUrl);
      return { id, blob, name: image.name || "生活图片", type: image.type || blob.type, size: blob.size, createdAt: image.createdAt };
    };
    ui.migrationBusy = true;
    try {
      overlayRoot.innerHTML = `<div class="modal-backdrop"><section class="modal" data-stop-close><span class="modal-symbol">${icon("upload")}</span><h2>正在恢复账号数据</h2><p>请保持页面打开。完成前不会显示未校验的数据。</p></section></div>`;
      await cloud.clearAccountForRestore();
      await migrateLegacySnapshot(asSnapshot(backup), loader(backup));
      ui.pendingImport = null; ui.preImportBackup = null; overlayRoot.innerHTML = ""; go("today");
      setTimeout(() => showToast(`账号数据已恢复 · ${backup.moments.length} 条 Moment`), 0);
    } catch (error) {
      try {
        const previous = validateBackup(ui.preImportBackup);
        await cloud.clearAccountForRestore();
        await migrateLegacySnapshot(asSnapshot(previous), loader(previous));
        showToast("导入没有完成，账号原有内容已自动恢复");
      } catch (_) { showToast(`导入未完成。请使用刚刚下载的自动备份恢复：${error?.message || "未知错误"}`); }
      ui.pendingImport = null; ui.preImportBackup = null; overlayRoot.innerHTML = ""; render();
    } finally { ui.migrationBusy = false; }
  }

  function showClearLocalDataModal() {
    const localMode = !cloud.configured;
    overlayRoot.innerHTML = `<div class="modal-backdrop" data-action="close-modal"><section class="modal" role="dialog" aria-modal="true" aria-labelledby="clear-data-title" data-stop-close><span class="modal-symbol">${icon("trash")}</span><h2 id="clear-data-title">清空当前${localMode ? "浏览器" : "账号"}的数据？</h2><p>Moment、图片、自定义类型、标签和每日收束都会被移除。默认记录类型保留。这个操作无法撤销。</p><div class="import-impact"><strong>建议先导出一份备份</strong><small>备份文件可以在之后完整恢复。</small></div><div class="modal-actions"><button class="btn" data-action="close-modal">取消</button><button class="btn btn-danger" data-action="confirm-clear-local-data">确认清空${localMode ? "本地" : "账号"}</button></div></section></div>`;
  }

  function showSeedDataModal() {
    overlayRoot.innerHTML = `<div class="modal-backdrop" data-action="close-modal"><section class="modal" role="dialog" aria-modal="true" aria-labelledby="seed-data-title" data-stop-close><span class="modal-symbol">${icon("memory")}</span><h2 id="seed-data-title">载入初始化测试数据？</h2><p>将加入一组带日期、类型、标签和来源关系的示例 Moment。</p><div class="import-impact"><strong>当前浏览器中的内容将被替换</strong><small>如需保留，请先取消并导出当前备份。</small></div><div class="modal-actions"><button class="btn" data-action="close-modal">取消</button><button class="btn btn-primary" data-action="confirm-seed-data">确认载入</button></div></section></div>`;
  }

  async function clearLocalDataConfirmed() {
    try {
      if (cloud.configured) await cloud.clearAccountForRestore();
      else { await clearImageAssets(); clearPrototypeUserData(false); }
    } catch (error) { showToast(error?.message || "数据清空失败"); return; }
    overlayRoot.innerHTML = "";
    go("today");
    setTimeout(() => showToast(`${cloud.configured ? "账号" : "本地"}数据已清空`), 0);
  }

  function seedDataConfirmed() {
    seedDemoSpace();
    overlayRoot.innerHTML = "";
    go("today");
    setTimeout(() => showToast("初始化测试数据已载入"), 0);
  }

  function showDeleteModal(id) {
    overlayRoot.innerHTML = `<div class="modal-backdrop" data-action="close-modal"><section class="modal" role="dialog" aria-modal="true" aria-labelledby="delete-title" data-stop-close>
      <h2 id="delete-title">删除这条 Moment？</h2>
      <p>确认后它会从正常内容中移除，并短时间提供“撤销删除”。不会建立回收站。</p>
      <div class="modal-actions"><button class="btn" data-action="close-modal">取消</button><button class="btn btn-danger" data-action="confirm-delete" data-id="${esc(id)}">确认删除</button></div>
    </section></div>`;
  }

  function showLogoutModal() {
    overlayRoot.innerHTML = `<div class="modal-backdrop" data-action="close-modal"><section class="modal" role="dialog" aria-modal="true" aria-labelledby="logout-title" data-stop-close>
      <span class="modal-symbol">${icon("logout")}</span><h2 id="logout-title">退出当前空间？</h2>
      <p>退出后会停止展示当前账号的个人内容。本机未提交草稿仍会保留，重新登录后可以继续。</p>
      <div class="modal-actions"><button class="btn" data-action="close-modal">先留下</button><button class="btn btn-danger" data-action="confirm-logout">确认退出</button></div>
    </section></div>`;
  }

  function showNameModal(kind, id = "", original = "") {
    const item = kind === "type" && id ? getTypeItems().find((type) => type.id === id) : kind === "tag" && id ? getTagItems().find((tag) => tag.id === id) : null;
    const current = item?.name || original || "";
    const noun = kind === "type" ? "记录类型" : "主题标签";
    overlayRoot.innerHTML = `<div class="modal-backdrop" data-action="close-modal"><section class="modal manage-modal" role="dialog" aria-modal="true" aria-labelledby="manage-title" data-stop-close>
      <h2 id="manage-title">${current ? `修改${noun}` : `新建${noun}`}</h2><p>${kind === "type" ? "类型用来表达为什么记录；一条 Moment 只能选择一个。" : "标签用来描述记录与什么有关，一条 Moment 可以有多个。"}</p>
      <label class="field-label" for="manage-name-input">名称</label><input class="input" id="manage-name-input" value="${esc(current)}" maxlength="24" autocomplete="off" />
      <div class="modal-error" id="manage-name-error" role="alert"></div>
      <div class="modal-actions"><button class="btn" data-action="close-modal">取消</button><button class="btn btn-primary" data-action="save-managed-name" data-kind="${kind}" data-id="${esc(id)}" data-original="${esc(current)}">保存</button></div>
    </section></div>`;
    setTimeout(() => document.getElementById("manage-name-input")?.focus(), 0);
  }

  function showManageDeleteModal(kind, id, name) {
    const noun = kind === "type" ? "记录类型" : "主题标签";
    const item = kind === "type" ? getTypeItems().find((entry) => entry.id === id) : getTagItems().find((entry) => entry.id === id);
    const used = kind === "type" ? getMoments().some((moment) => moment.recordTypeId === id) : getMoments().some((moment) => (moment.tagIds || []).includes(id));
    const mustArchive = used || Boolean(item?.isSystem || item?.isDefault);
    overlayRoot.innerHTML = `<div class="modal-backdrop" data-action="close-modal"><section class="modal" role="dialog" aria-modal="true" aria-labelledby="manage-delete-title" data-stop-close>
      <h2 id="manage-delete-title">${mustArchive ? "停用" : "永久删除"}“${esc(name)}”？</h2><p>${mustArchive ? `它已被历史 Moment 使用或属于系统类型，将停止出现在新记录选择器中；历史关联继续保留。` : `它从未被任何 Moment 使用，可以永久删除。这个操作无法撤销。`}</p>
      <div class="modal-actions"><button class="btn" data-action="close-modal">取消</button><button class="btn btn-danger" data-action="confirm-manage-delete" data-kind="${kind}" data-id="${esc(id || "")}" data-name="${esc(name)}">确认${mustArchive ? "停用" : "删除"}</button></div>
    </section></div>`;
  }

  async function saveManagedName(kind, id, original) {
    const input = document.getElementById("manage-name-input");
    const error = document.getElementById("manage-name-error");
    const name = String(input?.value || "").trim();
    if (!name) { if (error) error.textContent = "请输入名称"; return; }
    if (kind === "type") {
      const duplicate = getTypeItems().some((item) => item.name === name && item.id !== id);
      if (duplicate) { if (error) error.textContent = "已存在同名类型"; return; }
      const types = getTypeItems();
      if (cloud.state.user) {
        const existing = types.find((item) => item.id === id);
        if (existing?.isSystem) return;
        try { await cloud.saveType(existing ? { ...existing, name } : { name, description: "自定义记录类型", sortOrder: types.length * 10 + 10, isArchived: false, isSystem: false }); }
        catch (saveError) { if (error) error.textContent = saveError.message || "保存失败"; return; }
      } else {
      if (id) {
        const index = types.findIndex((item) => item.id === id);
        if (index < 0) return;
        if (types[index].isDefault) return;
        types[index] = { ...types[index], name };
      } else {
        const newItem = { id: `type:${makeId()}`, name, description: "自定义记录类型", isArchived: false, isDefault: false, createdAt: new Date().toISOString() };
        types.push(newItem);
        write(STORAGE.typeOrder, [...types.map((item) => item.id)]);
      }
      write(STORAGE.types, types);
      }
    } else {
      const tags = getTagItems();
      if (tags.some((tag) => tag.name === name && tag.id !== id)) { if (error) error.textContent = "已存在同名标签"; return; }
      if (cloud.state.user) {
        const existing = tags.find((tag) => tag.id === id);
        try { await cloud.saveTag(existing ? { ...existing, name } : { name, isArchived: false }); }
        catch (saveError) { if (error) error.textContent = saveError.message || "保存失败"; return; }
      } else {
      if (id) {
        const index = tags.findIndex((tag) => tag.id === id);
        if (index < 0) return;
        tags[index] = { ...tags[index], name };
      } else {
        tags.push({ id: `tag:${makeId()}`, name, isArchived: false, createdAt: new Date().toISOString() });
      }
      write(STORAGE.tags, tags);
      }
    }
    overlayRoot.innerHTML = "";
    if (route().name === "types") renderTypesPage();
    else if (route().name === "tags") renderTagsPage();
    else renderMy();
    showToast(`${kind === "type" ? "记录类型" : "主题标签"}已保存`);
  }

  async function deleteManaged(kind, id, name) {
    const noun = kind === "type" ? "记录类型" : "主题标签";
    const item = kind === "type" ? getTypeItems().find((entry) => entry.id === id) : getTagItems().find((entry) => entry.id === id);
    const used = kind === "type" ? getMoments().some((moment) => moment.recordTypeId === id) : getMoments().some((moment) => (moment.tagIds || []).includes(id));
    const archive = used || Boolean(item?.isSystem || item?.isDefault);
    try {
      if (cloud.state.user) {
        if (kind === "type") archive ? await cloud.saveType({ ...item, isArchived: true }) : await cloud.deleteType(id);
        else archive ? await cloud.saveTag({ ...item, isArchived: true }) : await cloud.deleteTag(id);
      } else if (kind === "type") {
        write(STORAGE.types, archive ? getTypeItems().map((entry) => entry.id === id ? { ...entry, isArchived: true } : entry) : getTypeItems().filter((entry) => entry.id !== id));
      } else {
        write(STORAGE.tags, archive ? getTagItems().map((entry) => entry.id === id ? { ...entry, isArchived: true } : entry) : getTagItems().filter((entry) => entry.id !== id));
      }
    } catch (error) { showToast(error?.message || `${noun}处理失败`); return; }
    overlayRoot.innerHTML = "";
    if (route().name === "types") renderTypesPage();
    else if (route().name === "tags") renderTagsPage();
    else renderMy();
    showToast(archive ? `${kind === "type" ? "记录类型" : "主题标签"}已停用，历史关联仍保留` : `${kind === "type" ? "记录类型" : "主题标签"}已永久删除`);
  }

  async function moveType(id, direction) {
    const items = getTypeItems();
    const index = items.findIndex((item) => item.id === id);
    const nextIndex = direction === "up" ? index - 1 : index + 1;
    if (index < 0 || nextIndex < 0 || nextIndex >= items.length) return;
    const order = items.map((item) => item.id);
    [order[index], order[nextIndex]] = [order[nextIndex], order[index]];
    if (cloud.state.user) await cloud.reorderTypes(order);
    else write(STORAGE.typeOrder, order);
    if (route().name === "types") renderTypesPage();
    else renderMy();
  }

  async function localAssetsFor(refs) {
    const assets = [];
    for (const ref of refs || []) {
      if (!String(ref).startsWith("img:")) continue;
      const stored = await getImageAsset(ref);
      if (!stored?.blob) throw new Error(`图片“${imageLabel(ref)}”无法读取，请重新选择`);
      assets.push({ ref, blob: stored.blob, name: stored.name, mimeType: stored.type, width: null, height: null });
    }
    return assets;
  }

  async function persistMoment(moment) {
    if (!cloud.configured) {
      const normalized = normalizeMoment(moment);
      const moments = getMoments();
      const index = moments.findIndex((item) => item.id === normalized.id);
      if (index >= 0) moments[index] = normalized;
      else moments.push(normalized);
      setMoments(moments);
      return normalized;
    }
    if (!cloud.state.user) throw new Error("请先登录");
    const localAssets = await localAssetsFor(moment.images);
    const cloudImages = (moment.images || []).filter((ref) => String(ref).startsWith("cloud:"));
    const saved = await cloud.saveMoment({ ...moment, images: cloudImages }, localAssets);
    await Promise.all(localAssets.map((asset) => deleteImageAsset(asset.ref).catch(() => {})));
    return saved;
  }

  async function saveReply(sourceId, stayInMemory = false) {
    const source = getMoments().find((moment) => moment.id === sourceId);
    const draft = getReplyDraft(sourceId);
    if (!source || !draft.content.trim()) return;
    const pendingId = ui.pendingReplyIds.get(sourceId) || makeId();
    ui.pendingReplyIds.set(sourceId, pendingId);
    const moment = { id: pendingId, content: draft.content.trim(), images: draft.images, recordTypeId: draft.recordTypeId || null, tagIds: draft.tagIds, occurredAt: draft.occurredAt || nowLocalValue(), createdAt: new Date().toISOString(), sourceMomentId: sourceId };
    try {
      await persistMoment(moment);
    } catch (error) {
      showToast(error?.message || "保存失败，现在的想法仍保留在草稿中");
      return;
    }
    remove(STORAGE.replyDraft);
    ui.pendingReplyIds.delete(sourceId);
    ui.replyExtrasOpen = false;
    if (stayInMemory) {
      ui.memoryLinkedMomentId = moment.id;
      ui.memoryReplyOpen = false;
      renderMemory();
      showToast("已写下，并和过去的记录相连");
      return;
    }
    ui.detailStack = [];
    ui.detailReturn = "memory";
    go(`moment/${moment.id}`);
    setTimeout(() => showToast("新的 Moment 已保存"), 0);
  }

  async function saveMoment(fromGlobal = false) {
    const draft = getDraft();
    ui.inlineError = "";
    if (!draft.content.trim()) return;
    if (new Date(draft.occurredAt) > new Date()) {
      ui.inlineError = "发生时间不能晚于现在。";
      return fromGlobal ? showGlobalRecorder() : (route().name === "record" ? renderRecord() : renderToday());
    }
    if (read(STORAGE.failNextSave, false)) {
      write(STORAGE.failNextSave, false);
      ui.inlineError = "保存失败。草稿和已选内容仍在，请重试。";
      return fromGlobal ? showGlobalRecorder() : (route().name === "record" ? renderRecord() : renderToday());
    }
    const createdAt = new Date().toISOString();
    const pendingId = ui.pendingSaveId || makeId();
    ui.pendingSaveId = pendingId;
    const moment = { id: pendingId, content: draft.content.trim(), images: draft.images, recordTypeId: draft.recordTypeId || null, tagIds: draft.tagIds, occurredAt: draft.occurredAt || nowLocalValue(), createdAt, sourceMomentId: null };
    try {
      const saved = await persistMoment(moment);
      moment.id = saved.id;
      moment.images = saved.images;
    } catch (error) {
      ui.inlineError = error?.message || "保存失败。正文、图片和已选内容仍在，请重试。";
      return fromGlobal ? showGlobalRecorder() : (route().name === "record" ? renderRecord() : renderToday());
    }
    remove(STORAGE.draft);
    ui.pendingSaveId = "";
    volatileDraft = null;
    ui.typeGuideOpen = false;
    ui.globalTypeGuideOpen = false;
    ui.promptTypesExpanded = false;
    ui.inlineError = "";
    ui.todaySelectedId = moment.id;
    ui.workspaceEditing = false;
    if (fromGlobal) {
      render();
      showToast("已留下");
      return;
    }
    const isToday = moment.occurredAt.slice(0, 10) === todayKey();
    const savedMessage = isToday ? "收好了，以后还会再遇见" : `已放进 ${moment.occurredAt.slice(0, 10)}`;
    if (route().name === "record") {
      go("today");
      setTimeout(() => showToast(savedMessage, "补充信息", `edit:${moment.id}`), 0);
      return;
    }
    renderToday();
    showToast(savedMessage, "补充信息", `edit:${moment.id}`);
  }

  async function deleteMoment(id) {
    if (ui.pendingDeleted) {
      const previous = ui.pendingDeleted;
      clearTimeout(ui.deleteTimer);
      ui.pendingDeleted = null;
      try { if (cloud.configured) await cloud.deleteMoment(previous.moment.id); }
      catch (_) {
        const restored = [...getMoments()];
        restored.splice(Math.min(previous.index, restored.length), 0, previous.moment);
        setMoments(restored);
        showToast("上一条删除没有完成，Moment 已恢复");
      }
    }
    const moments = getMoments();
    const index = moments.findIndex((m) => m.id === id);
    if (index < 0) return;
    const moment = moments[index];
    setMoments(moments.filter((m) => m.id !== id));
    ui.pendingDeleted = { moment, index };
    overlayRoot.innerHTML = "";
    clearTimeout(ui.deleteTimer);
    ui.deleteTimer = setTimeout(async () => {
      const pending = ui.pendingDeleted;
      ui.pendingDeleted = null;
      toastRoot.innerHTML = "";
      if (!pending || pending.moment.id !== id) return;
      try { if (cloud.configured) await cloud.deleteMoment(id); }
      catch (_) {
        const restored = [...getMoments()];
        restored.splice(Math.min(pending.index, restored.length), 0, pending.moment);
        setMoments(restored);
        render();
        showToast("删除没有完成，Moment 已恢复");
      }
    }, 10000);
    const returnPath = ui.detailReturn || "today";
    if (returnPath.startsWith("moment/")) {
      const parent = ui.detailStack.pop();
      ui.detailReturn = parent?.returnPath || "memory";
    } else {
      ui.detailStack = [];
    }
    if (route().name === returnPath) render();
    else go(returnPath);
    setTimeout(() => showToast("已删除", "撤销", "undo-delete"), 0);
  }

  function undoDelete() {
    if (!ui.pendingDeleted) return;
    const moments = getMoments();
    const restored = [...moments];
    restored.splice(Math.min(ui.pendingDeleted.index, restored.length), 0, ui.pendingDeleted.moment);
    setMoments(restored);
    clearTimeout(ui.deleteTimer);
    ui.pendingDeleted = null;
    render();
    showToast("Moment 已恢复");
  }

  async function addTag(target, raw, momentId = "", existingId = "") {
    const tag = String(raw || "").trim();
    let tagId = existingId;
    try { if (!tagId) tagId = await ensureTagExists(tag); }
    catch (error) { showToast(error?.message || "标签保存失败"); return; }
    if (!tagId) return;
    if (target === "draft" || target === "global") {
      const draft = getDraft();
      if (!draft.tagIds.includes(tagId)) saveDraft({ tagIds: [...draft.tagIds, tagId] });
      return target === "global" ? showGlobalRecorder() : (route().name === "record" ? renderRecord() : renderToday());
    }
    if (target === "reply") {
      const sourceId = momentId || route().id || ui.memoryCurrentId;
      const draft = getReplyDraft(sourceId);
      if (!draft.tagIds.includes(tagId)) saveReplyDraft(sourceId, { tagIds: [...draft.tagIds, tagId] });
      return route().name === "memory" ? renderMemory() : renderReply(sourceId);
    }
    const id = momentId || route().id;
    const moment = getMoments().find((m) => m.id === id);
    const data = getEditDraft(moment);
    if (!data.tagIds.includes(tagId)) saveEditDraft(id, { tagIds: [...data.tagIds, tagId] });
    if (target === "workspace") render();
    else renderDetail(id, true);
  }

  function saveClosureDraft(patch) {
    const date = todayKey();
    const drafts = read(STORAGE.closureDraft, {});
    const current = drafts[date] || getClosures()[date] || { answers: ["", "", ""], finalLine: "" };
    drafts[date] = { ...current, ...patch };
    write(STORAGE.closureDraft, drafts);
    const any = drafts[date].answers.some((x) => x.trim()) || drafts[date].finalLine.trim();
    const button = document.getElementById("closure-save");
    if (button) button.disabled = !any;
  }

  function authMessage(error) {
    const message = String(error?.message || error || "");
    if (/invalid login credentials/i.test(message)) return "邮箱或密码不正确";
    if (/email not confirmed/i.test(message)) return "请先打开邮箱中的确认链接，再回来登录";
    if (/user already registered/i.test(message)) return "这个邮箱已经注册，可以直接登录";
    if (/password/i.test(message) && /least/i.test(message)) return "密码至少需要 8 位";
    return message || "暂时无法完成，请稍后重试";
  }

  async function submitAuth() {
    if (ui.authBusy) return;
    const email = String(document.getElementById("auth-email")?.value || "").trim();
    const password = String(document.getElementById("auth-password")?.value || "");
    if (!email || password.length < 8) { ui.authError = "请填写有效邮箱和至少 8 位密码"; return renderLogin(); }
    ui.authBusy = true; ui.authError = ""; ui.authNotice = ""; renderLogin();
    try {
      if (ui.authMode === "register") {
        const result = await cloud.signUp(email, password);
        if (!result.session) {
          ui.authNotice = "注册成功。请打开邮箱确认账号，然后回来登录。";
          ui.authMode = "login";
        } else go("today");
      } else {
        await cloud.signIn(email, password);
        go("today");
        setTimeout(showLegacyMigrationOffer, 120);
      }
    } catch (error) { ui.authError = authMessage(error); }
    finally { ui.authBusy = false; render(); }
  }

  document.addEventListener("submit", (event) => {
    if (event.target.id !== "auth-form") return;
    event.preventDefault();
    submitAuth();
  });

  document.addEventListener("input", (event) => {
    const el = event.target;
    if (el.id === "time-search") {
      ui.timeQuery = el.value;
      ui.timeSelectedDate = "all";
      clearTimeout(ui.timeSearchTimer);
      ui.timeSearchTimer = setTimeout(() => {
        renderTime();
        const input = document.getElementById("time-search");
        input?.focus();
        input?.setSelectionRange(input.value.length, input.value.length);
      }, 180);
      return;
    }
    if (el.id === "draft-body") {
      saveDraft({ body: el.value });
      updateSaveButton();
    }
    if (el.id === "global-draft-body") {
      saveDraft({ body: el.value });
      updateGlobalRecorder();
    }
    if (el.id === "edit-body") {
      const id = el.dataset.editId;
      saveEditDraft(id, { body: el.value });
      const button = document.querySelector('[data-action="save-edit"]');
      if (button) button.disabled = !el.value.trim();
    }
    if (el.id === "reply-body") saveReplyDraft(el.dataset.replySource, { body: el.value });
    if (el.id === "memory-reply-body") {
      const draft = saveReplyDraft(el.dataset.replySource, { body: el.value });
      const button = document.getElementById("memory-reply-save");
      if (button) button.disabled = !draft.body.trim();
    }
    if (el.id === "workspace-edit-body") {
      saveEditDraft(el.dataset.workspaceId, { body: el.value });
      const button = document.querySelector('[data-action="workspace-save-edit"]');
      if (button) button.disabled = !el.value.trim();
    }
    if (el.matches("[data-closure-answer]")) {
      const date = todayKey();
      const drafts = read(STORAGE.closureDraft, {});
      const current = drafts[date] || getClosures()[date] || { answers: ["", "", ""], finalLine: "" };
      const answers = [...current.answers];
      answers[Number(el.dataset.closureAnswer)] = el.value;
      saveClosureDraft({ answers });
    }
    if (el.matches("[data-closure-final]")) saveClosureDraft({ finalLine: el.value });
  });

  document.addEventListener("change", async (event) => {
    const el = event.target;
    if (el.matches("[data-image-upload]")) {
      await saveImageFiles(el.files || [], el.dataset.imageUpload, el.dataset.momentId || "");
      return;
    }
    if (el.matches("[data-backup-import]")) {
      await readBackupFile(el.files?.[0]);
      el.value = "";
      return;
    }
    if (el.id === "time-filter-type" || el.id === "time-filter-tag" || el.id === "time-filter-from" || el.id === "time-filter-to") {
      if (el.id === "time-filter-type") ui.timeFilterType = el.value;
      if (el.id === "time-filter-tag") ui.timeFilterTag = el.value;
      if (el.id === "time-filter-from") ui.timeFilterFrom = el.value;
      if (el.id === "time-filter-to") ui.timeFilterTo = el.value;
      ui.timeSelectedDate = "all";
      renderTime();
      return;
    }
    if (el.matches('[data-occurrence="draft"]')) saveDraft({ occurrenceAt: el.value });
    if (el.matches('[data-occurrence="global"]')) saveDraft({ occurrenceAt: el.value });
    if (el.matches('[data-occurrence="edit"]')) saveEditDraft(route().id, { occurrenceAt: el.value });
    if (el.matches('[data-occurrence="workspace"]')) saveEditDraft(el.dataset.momentId, { occurrenceAt: el.value });
    if (el.matches('[data-occurrence="reply"]')) saveReplyDraft(el.dataset.momentId || route().id || ui.memoryCurrentId, { occurredAt: el.value });
    if (el.matches("[data-editor-type]")) {
      const target = el.dataset.editorType;
      const id = el.dataset.momentId || route().id || ui.memoryCurrentId;
      if (target === "draft" || target === "global") saveDraft({ recordTypeId: el.value });
      else if (target === "reply") saveReplyDraft(id, { recordTypeId: el.value });
      else saveEditDraft(id, { recordTypeId: el.value });
    }
    if (el.matches("[data-edit-type]")) saveEditDraft(el.dataset.editType, { recordTypeId: el.value });
    if (el.matches("[data-reply-type]")) saveReplyDraft(el.dataset.replyType, { recordTypeId: el.value });
    if (el.matches("[data-workspace-type]")) saveEditDraft(el.dataset.workspaceType, { recordTypeId: el.value });
    if (el.matches("[data-workspace-occurrence]")) saveEditDraft(el.dataset.workspaceOccurrence, { occurrenceAt: el.value });
  });

  document.addEventListener("keydown", (event) => {
    const el = event.target;
    const typing = el.matches("input, textarea, select, [contenteditable='true']");
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      const save = [...document.querySelectorAll('[data-action="save-global-moment"], [data-action="workspace-save-edit"], [data-action="save-memory-reply"], [data-action="save-edit"], [data-action="save-moment"], [data-action="save-closure"]')].find((button) => !button.disabled && button.offsetParent !== null);
      if (save) { event.preventDefault(); save.click(); }
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
      event.preventDefault();
      if (route().name !== "time") {
        go("time");
        setTimeout(() => document.getElementById("time-search")?.focus(), 80);
      } else document.getElementById("time-search")?.focus();
      return;
    }
    if (event.key === "Escape") {
      if (overlayRoot.innerHTML) {
        event.preventDefault();
        overlayRoot.innerHTML = "";
        ui.imageUploadError = "";
        ui.pendingImport = null;
        render();
        return;
      }
      if (ui.workspaceEditing) {
        event.preventDefault();
        const id = route().name === "today" ? ui.todaySelectedId : ui.workspaceMomentId || ui.memoryCurrentId;
        if (id) { remove(editKey(id)); volatileEditDrafts.delete(id); }
        ui.workspaceEditing = false;
        render();
        return;
      }
      if (route().name === "moment" && route().mode === "edit") { event.preventDefault(); go(`moment/${route().id}`); return; }
      if (route().name === "memory" && (ui.memoryMetaOpen || ui.memoryDayOpen || ui.memoryReplyOpen)) {
        event.preventDefault(); ui.memoryMetaOpen = false; ui.memoryDayOpen = false; ui.memoryReplyOpen = false; renderMemory(); return;
      }
    }
    if (event.key === "/" && !typing && route().name === "today") {
      event.preventDefault(); document.getElementById("draft-body")?.focus(); return;
    }
    if (el.matches("[data-tag-input]") && (event.key === "Enter" || event.key === ",")) {
      event.preventDefault();
      const target = el.dataset.tagInput;
      const value = el.value;
      el.value = "";
      addTag(target, value, el.dataset.momentId || "");
    }
  });

  document.addEventListener("click", async (event) => {
    const target = event.target.closest("button, [data-action]");
    if (!target) return;
    if (target.hasAttribute("data-stop-close")) return;
    const action = target.dataset.action;
    if (target.dataset.returnPath) {
      const returnPath = target.dataset.returnPath;
      if (returnPath.startsWith("moment/")) {
        const parent = ui.detailStack.pop();
        ui.detailReturn = parent?.returnPath || "memory";
      } else {
        ui.detailStack = [];
      }
      return go(returnPath);
    }
    if (target.dataset.nav) {
      ui.detailStack = [];
      if (target.dataset.nav === "time") {
        ui.timeView = "timeline";
        if (route().name === "time") return renderTime();
      }
      return go(target.dataset.nav);
    }
    if (target.dataset.momentId) {
      const current = route().name;
      if (target.dataset.returnPathDirect) {
        ui.detailStack = [];
        ui.detailReturn = target.dataset.returnPathDirect;
        return go(`moment/${target.dataset.momentId}`);
      }
      if (target.dataset.return) {
        ui.detailStack.push({ momentId: route().id, returnPath: ui.detailReturn });
        ui.detailReturn = target.dataset.return;
      } else {
        ui.detailStack = [];
        ui.detailReturn = current === "time" ? "time" : current === "date" ? `date/${route().id}` : current === "memory" ? "memory" : "today";
      }
      return go(`moment/${target.dataset.momentId}`);
    }
    if (target.dataset.toastAction) {
      const value = target.dataset.toastAction;
      if (value === "undo-delete") return undoDelete();
      if (value.startsWith("edit:")) { ui.detailReturn = "today"; return go(`moment/${value.slice(5)}/edit`); }
    }
    if (action === "auth-switch") { ui.authMode = ui.authMode === "login" ? "register" : "login"; ui.authError = ""; ui.authNotice = ""; return renderLogin(); }
    if (action === "dismiss-migration") { sessionStorage.setItem(`qymj.webv1.migration-dismissed:${cloud.state.user?.id}`, "1"); overlayRoot.innerHTML = ""; return; }
    if (action === "migrate-local-data") return runLegacyMigration();
    if (action === "keep-legacy-data") { sessionStorage.setItem(`qymj.webv1.migration-dismissed:${cloud.state.user?.id}`, "1"); overlayRoot.innerHTML = ""; return; }
    if (action === "clear-migrated-data") return clearMigratedLocalData();
    if (action === "start-demo") return showSeedDataModal();
    if (action === "start-empty") { startEmptySpace(); return go("today"); }
    if (action === "brand-today") return go("today");
    if (action === "logout") return showLogoutModal();
    if (action === "confirm-logout") {
      try {
        if (ui.pendingDeleted) {
          const pending = ui.pendingDeleted; clearTimeout(ui.deleteTimer); ui.pendingDeleted = null;
          await cloud.deleteMoment(pending.moment.id);
        }
        await cloud.signOut();
      } catch (error) { showToast(authMessage(error)); return; }
      overlayRoot.innerHTML = ""; return go("login");
    }
    if (action === "gallery-open") return applyGalleryPreset(target.dataset.preset);
    if (action === "open-settings-page") return go(target.dataset.page);
    if (action === "workspace-open") {
      ui.workspaceEditing = false;
      if (route().name === "today") { ui.todaySelectedId = target.dataset.id; return renderToday(); }
      ui.workspaceMomentId = target.dataset.id;
      return renderTime();
    }
    if (action === "workspace-edit") {
      ui.workspaceEditing = true;
      ui.workspaceMomentId = target.dataset.id;
      if (route().name === "today") ui.todaySelectedId = target.dataset.id;
      return render();
    }
    if (action === "workspace-cancel-edit") {
      remove(editKey(target.dataset.id));
      volatileEditDrafts.delete(target.dataset.id);
      ui.workspaceEditing = false;
      return render();
    }
    if (action === "workspace-save-edit") {
      const id = target.dataset.id;
      const moments = getMoments();
      const index = moments.findIndex((moment) => moment.id === id);
      if (index < 0) return;
      const data = getEditDraft(moments[index]);
      if (!data.body.trim()) return;
      if (new Date(data.occurrenceAt) > new Date()) return showToast("发生时间不能晚于现在");
      moments[index] = { ...moments[index], ...data, body: data.body.trim() };
      try { await persistMoment(moments[index]); } catch (error) { showToast(error?.message || "保存失败，修改仍保留在编辑区"); return; }
      remove(editKey(id));
      volatileEditDrafts.delete(id);
      ui.workspaceEditing = false;
      render();
      showToast("修改已保存");
      return;
    }
    if (action === "workspace-delete") {
      ui.detailReturn = target.dataset.context === "time" ? "time" : target.dataset.context === "memory" ? "memory" : "today";
      return showDeleteModal(target.dataset.id);
    }
    if (action === "workspace-open-source" || action === "workspace-open-related") {
      const source = getMoments().find((moment) => moment.id === target.dataset.id);
      if (!source) return;
      ui.timeSelectedDate = String(source.occurrenceAt).slice(0, 10);
      ui.workspaceMomentId = source.id;
      ui.workspaceEditing = false;
      return route().name === "time" ? renderTime() : go("time");
    }
    if (action === "open-full-record") return go("record");
    if (action === "open-global-record") { ui.inlineError = ""; return showGlobalRecorder(); }
    if (action === "return-global-record") return showGlobalRecorder();
    if (action === "time-view") { ui.timeView = target.dataset.view; return renderTime(); }
    if (action === "toggle-time-filters") { ui.timeFilterOpen = !ui.timeFilterOpen; return renderTime(); }
    if (action === "clear-time-filters") { ui.timeQuery = ""; ui.timeFilterType = ""; ui.timeFilterTag = ""; ui.timeFilterFrom = ""; ui.timeFilterTo = ""; ui.timeSelectedDate = "all"; return renderTime(); }
    if (action === "time-date-select") { ui.timeSelectedDate = target.dataset.date; ui.workspaceMomentId = ""; ui.workspaceEditing = false; return renderTime(); }
    if (action === "month-prev" || action === "month-next") {
      ui.calendarMonth = monthKeyOffset(ui.calendarMonth, action === "month-prev" ? -1 : 1);
      return renderTime();
    }
    if (action === "select-date") { ui.selectedDate = target.dataset.date; return renderTime(); }
    if (action === "open-date") { ui.dateReturn = "time"; return go(`date/${target.dataset.date}`); }
    if (action === "memory-day") { ui.detailStack = []; ui.dateReturn = "memory"; return go(`date/${target.dataset.date}`); }
    if (action === "memory-mode") {
      ui.memoryMode = target.dataset.mode;
      if (ui.memoryMode === "type" && !ui.memoryType) {
        const historicalTypes = getMoments().filter((moment) => String(moment.occurredAt).slice(0, 10) < todayKey() && moment.recordTypeId).map((moment) => moment.recordTypeId);
        ui.memoryType = ["type:happiness", "type:learned", "type:thought"].find((typeId) => historicalTypes.includes(typeId)) || historicalTypes[0] || "";
      }
      ui.memoryCurrentId = ""; ui.memorySeen.clear(); ui.memoryMetaOpen = false; ui.memoryDayOpen = false; ui.memoryReplyOpen = false; ui.memoryLinkedMomentId = ""; ui.workspaceEditing = false;
      return renderMemory();
    }
    if (action === "memory-type") { ui.memoryMode = "type"; ui.memoryType = target.dataset.type; ui.memoryCurrentId = ""; ui.memorySeen.clear(); ui.memoryMetaOpen = false; ui.memoryDayOpen = false; ui.memoryReplyOpen = false; return renderMemory(); }
    if (action === "next-memory") { ui.memoryMetaOpen = false; ui.memoryDayOpen = false; ui.memoryReplyOpen = false; ui.memoryLinkedMomentId = ""; ui.workspaceEditing = false; chooseMemoryCandidate(true); return renderMemory(); }
    if (action === "memory-toggle-meta") { ui.memoryMetaOpen = true; ui.memoryDayOpen = false; ui.memoryReplyOpen = false; return renderMemory(); }
    if (action === "memory-toggle-day") { ui.memoryDayOpen = true; ui.memoryMetaOpen = false; ui.memoryReplyOpen = false; return renderMemory(); }
    if (action === "memory-toggle-reply") { ui.memoryReplyOpen = true; ui.memoryMetaOpen = false; ui.memoryDayOpen = false; return renderMemory(); }
    if (action === "toggle-memory-reply-extras") { const id = target.dataset.id; const draft = getReplyDraft(id); saveReplyDraft(id, { expanded: !draft.expanded }); return renderMemory(); }
    if (action === "memory-close-panel") { ui.memoryMetaOpen = false; ui.memoryDayOpen = false; ui.memoryReplyOpen = false; ui.workspaceEditing = false; return renderMemory(); }
    if (action === "memory-day-moment") { ui.memoryCurrentId = target.dataset.id; ui.memoryDayOpen = false; ui.memoryMetaOpen = false; ui.memoryReplyOpen = false; return renderMemory(); }
    if (action === "open-memory") { ui.detailStack = []; ui.detailReturn = "memory"; return go(`moment/${target.dataset.id}`); }
    if (action === "reply-memory") return go(`continue/${target.dataset.id}`);
    if (action === "toggle-reply-extras") { ui.replyExtrasOpen = !ui.replyExtrasOpen; return renderReply(route().id); }
    if (action === "save-reply") return saveReply(target.dataset.id);
    if (action === "save-memory-reply") return saveReply(target.dataset.id, true);
    if (action === "export-backup") return exportBackup();
    if (action === "trigger-image-upload") { event.preventDefault(); document.getElementById("image-file-input")?.click(); return; }
    if (action === "trigger-backup-import") { event.preventDefault(); document.getElementById("backup-import")?.click(); return; }
    if (action === "confirm-import-backup") return applyImportBackup();
    if (action === "ask-clear-local-data") return showClearLocalDataModal();
    if (action === "confirm-clear-local-data") return clearLocalDataConfirmed();
    if (action === "confirm-seed-data") return seedDataConfirmed();
    if (action === "open-name-modal") return showNameModal(target.dataset.kind, target.dataset.id, target.dataset.name);
    if (action === "save-managed-name") return saveManagedName(target.dataset.kind, target.dataset.id, target.dataset.original);
    if (action === "ask-manage-delete") return showManageDeleteModal(target.dataset.kind, target.dataset.id, target.dataset.name);
    if (action === "confirm-manage-delete") return deleteManaged(target.dataset.kind, target.dataset.id, target.dataset.name);
    if (action === "toggle-manage-panel") { ui.myPanel = ui.myPanel === target.dataset.panel ? "" : target.dataset.panel; return renderMy(); }
    if (action === "toggle-managed-archive") {
      const kind = target.dataset.kind;
      const id = target.dataset.id;
      if (kind === "type") {
        const item = getTypeItems().find((entry) => entry.id === id);
        if (cloud.state.user) await cloud.saveType({ ...item, isArchived: !item.isArchived });
        else write(STORAGE.types, getTypeItems().map((entry) => entry.id === id ? { ...entry, isArchived: !entry.isArchived } : entry));
      } else {
        const item = getTagItems().find((entry) => entry.id === id);
        if (cloud.state.user) await cloud.saveTag({ ...item, isArchived: !item.isArchived });
        else write(STORAGE.tags, getTagItems().map((entry) => entry.id === id ? { ...entry, isArchived: !entry.isArchived } : entry));
      }
      return kind === "type" ? renderTypesPage() : renderTagsPage();
    }
    if (action === "toggle-type-hidden") {
      const hidden = read(STORAGE.hiddenTypes, []);
      const name = target.dataset.name;
      write(STORAGE.hiddenTypes, hidden.includes(name) ? hidden.filter((item) => item !== name) : [...hidden, name]);
      return renderMy();
    }
    if (action === "move-type") return moveType(target.dataset.id, target.dataset.direction);
    if (action === "toggle-prompt-types") { ui.promptTypesExpanded = !ui.promptTypesExpanded; return renderToday(); }
    if (action === "choose-prompt-type") {
      const draft = getDraft();
      saveDraft({ type: draft.type === target.dataset.type ? "" : target.dataset.type });
      renderToday();
      setTimeout(() => document.getElementById("draft-body")?.focus(), 0);
      return;
    }
    if (action === "toggle-types") { ui.typeGuideOpen = !ui.typeGuideOpen; return route().name === "record" ? renderRecord() : renderToday(); }
    if (action === "toggle-expand") { const d = getDraft(); saveDraft({ expanded: !d.expanded }); return route().name === "record" ? go("today") : renderToday(); }
    if (action === "toggle-global-types") { ui.globalTypeGuideOpen = !ui.globalTypeGuideOpen; return showGlobalRecorder(); }
    if (action === "toggle-global-expand") { const d = getDraft(); saveDraft({ expanded: !d.expanded }); return showGlobalRecorder(); }
    if (action === "choose-type") {
      const name = target.dataset.type;
      if (target.dataset.target === "draft") { saveDraft({ type: name }); ui.typeGuideOpen = false; return route().name === "record" ? renderRecord() : renderToday(); }
      if (target.dataset.target === "global") { saveDraft({ type: name }); ui.globalTypeGuideOpen = false; return showGlobalRecorder(); }
    }
    if (action === "clear-type") {
      saveDraft({ type: "" });
      if (target.dataset.target === "global") { ui.globalTypeGuideOpen = false; return showGlobalRecorder(); }
      ui.typeGuideOpen = false;
      return route().name === "record" ? renderRecord() : renderToday();
    }
    if (action === "save-moment") return saveMoment();
    if (action === "save-global-moment") return saveMoment(true);
    if (action === "open-image-viewer") return showImageViewer(target.dataset.image);
    if (action === "open-images") { ui.imageUploadError = ""; return showImagePicker(target.dataset.target, target.dataset.momentId || ""); }
    if (action === "choose-image") {
      const context = target.dataset.target;
      const image = target.dataset.image;
      if (context === "draft" || context === "global") {
        const draft = getDraft();
        if (draft.images.length < 3 && !draft.images.includes(image)) saveDraft({ images: [...draft.images, image], expanded: true });
        showImagePicker(context);
      } else if (context === "reply") {
        const id = target.dataset.momentId || route().id || ui.memoryCurrentId;
        const draft = getReplyDraft(id);
        if (draft.images.length < 3 && !draft.images.includes(image)) saveReplyDraft(id, { images: [...draft.images, image], expanded: true });
        showImagePicker("reply", id);
      } else {
        const id = route().id;
        const moment = getMoments().find((m) => m.id === id);
        const data = getEditDraft(moment);
        if (data.images.length < 3 && !data.images.includes(image)) saveEditDraft(id, { images: [...data.images, image] });
        showImagePicker("edit");
      }
      return;
    }
    if (action === "remove-image") {
      const image = target.dataset.image;
      if (String(image).startsWith("img:")) deleteImageAsset(image).catch(() => {});
      if (target.dataset.target === "draft") { const d = getDraft(); saveDraft({ images: d.images.filter((x) => x !== image) }); return overlayRoot.innerHTML ? showImagePicker("draft") : (route().name === "record" ? renderRecord() : renderToday()); }
      if (target.dataset.target === "global") { const d = getDraft(); saveDraft({ images: d.images.filter((x) => x !== image) }); return overlayRoot.innerHTML ? showImagePicker("global") : showGlobalRecorder(); }
      if (target.dataset.target === "edit") { const id = target.dataset.momentId || route().id; const m = getMoments().find((x) => x.id === id); const d = getEditDraft(m); saveEditDraft(id, { images: d.images.filter((x) => x !== image) }); return overlayRoot.innerHTML ? showImagePicker("edit", id) : renderDetail(id, true); }
      if (target.dataset.target === "workspace") { const id = target.dataset.momentId; const m = getMoments().find((x) => x.id === id); const d = getEditDraft(m); saveEditDraft(id, { images: d.images.filter((x) => x !== image) }); return overlayRoot.innerHTML ? showImagePicker("workspace", id) : render(); }
      if (target.dataset.target === "reply") { const id = target.dataset.momentId || route().id || ui.memoryCurrentId; const d = getReplyDraft(id); saveReplyDraft(id, { images: d.images.filter((x) => x !== image) }); return overlayRoot.innerHTML ? showImagePicker("reply", id) : (route().name === "memory" ? renderMemory() : renderReply(id)); }
    }
    if (action === "add-tag") return addTag(target.dataset.target, target.dataset.tag, target.dataset.momentId || "", target.dataset.tagId || "");
    if (action === "remove-tag") {
      const tagId = target.dataset.tagId || tagIdByName(target.dataset.tag);
      if (target.dataset.target === "draft") { const d = getDraft(); saveDraft({ tagIds: d.tagIds.filter((x) => x !== tagId) }); return route().name === "record" ? renderRecord() : renderToday(); }
      if (target.dataset.target === "global") { const d = getDraft(); saveDraft({ tagIds: d.tagIds.filter((x) => x !== tagId) }); return showGlobalRecorder(); }
      if (target.dataset.target === "reply") { const id = target.dataset.momentId || route().id || ui.memoryCurrentId; const d = getReplyDraft(id); saveReplyDraft(id, { tagIds: d.tagIds.filter((x) => x !== tagId) }); return route().name === "memory" ? renderMemory() : renderReply(id); }
      const id = target.dataset.momentId || route().id; const m = getMoments().find((x) => x.id === id); const d = getEditDraft(m); saveEditDraft(id, { tagIds: d.tagIds.filter((x) => x !== tagId) }); return target.dataset.target === "workspace" ? render() : renderDetail(id, true);
    }
    if (action === "close-modal") { overlayRoot.innerHTML = ""; ui.imageUploadError = ""; ui.pendingImport = null; if (route().name === "record") return renderRecord(); if (route().name === "today") return renderToday(); if (route().name === "time") return renderTime(); if (route().name === "memory") return renderMemory(); return; }
    if (action === "edit-moment") return go(`moment/${target.dataset.id}/edit`);
    if (action === "exit-edit") return go(`moment/${target.dataset.id}`);
    if (action === "discard-edit") { remove(editKey(target.dataset.id)); volatileEditDrafts.delete(target.dataset.id); return renderDetail(target.dataset.id, true); }
    if (action === "save-edit") {
      const id = target.dataset.id;
      const moments = getMoments();
      const index = moments.findIndex((m) => m.id === id);
      if (index < 0) return;
      const data = getEditDraft(moments[index]);
      if (!data.body.trim()) return;
      if (new Date(data.occurrenceAt) > new Date()) { showToast("发生时间不能晚于现在"); return; }
      moments[index] = { ...moments[index], ...data, body: data.body.trim() };
      try { await persistMoment(moments[index]); } catch (error) { showToast(error?.message || "保存失败，修改仍保留在编辑区"); return; }
      remove(editKey(id)); volatileEditDrafts.delete(id); go(`moment/${id}`); setTimeout(() => showToast("修改已保存"), 0); return;
    }
    if (action === "ask-delete") return showDeleteModal(target.dataset.id);
    if (action === "confirm-delete") return deleteMoment(target.dataset.id);
    if (action === "open-closure") return go("closure");
    if (action === "save-closure") {
      const date = todayKey();
      const drafts = read(STORAGE.closureDraft, {});
      const data = drafts[date] || { answers: ["", "", ""], finalLine: "" };
      const any = data.answers.some((x) => x.trim()) || data.finalLine.trim();
      if (!any) return;
      try {
        if (cloud.configured) await cloud.saveClosure(date, data);
        else {
          const closures = getClosures();
          closures[date] = { ...data, date, updatedAt: new Date().toISOString() };
          write(STORAGE.closures, closures);
        }
      } catch (error) { showToast(error?.message || "今日收束保存失败，内容仍保留在草稿中"); return; }
      delete drafts[date]; write(STORAGE.closureDraft, drafts); go("today"); setTimeout(() => showToast("今日收束已保存"), 0); return;
    }
    if (action === "simulate-failure") { write(STORAGE.failNextSave, true); return showToast("下一次 Moment 保存将模拟失败"); }
  });

  window.addEventListener("hashchange", render);
  migrateStoredData();
  const imageObserver = new MutationObserver(queueImageHydration);
  imageObserver.observe(app, { childList: true, subtree: true });
  imageObserver.observe(overlayRoot, { childList: true, subtree: true });
  window.addEventListener("beforeunload", () => imageUrlCache.forEach((url) => URL.revokeObjectURL(url)));
  cloud.subscribe(({ reason }) => {
    if (!ui.migrationBusy) render();
    if ((reason === "initialized" || reason === "data-refreshed") && cloud.state.user && !ui.migrationBusy) setTimeout(showLegacyMigrationOffer, 120);
  });
  render();
  cloud.initialize().catch((error) => { cloud.state.ready = true; cloud.state.error = error?.message || "数据服务初始化失败"; render(); });
})();
